import type { CustomerTeamMember } from '@clockwise/clients/customer-team';
import type { Customer } from '@clockwise/clients/customers';
import type { Product } from '@clockwise/clients/products';
import {
  isAttentionStatus,
  isTimesheetEditable,
  TIMESHEET_STATUSES,
  type Timesheet,
  type TimesheetStatus,
} from '@clockwise/clients/timesheets';
import type { User } from '@clockwise/clients/users';
import {
  coversDate,
  type EngagementCandidate,
  type EngagementResolution,
  type EngagementVersion,
  membershipsContentKey,
  type TimesheetLockStage,
  timesheetLockStageFor,
  unionRolesCoveringDates,
} from '@clockwise/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ArrowUpDown, Lock, Plus, Trash2 } from 'lucide-react';
import {
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFetchErrorToast } from '@/hooks/use-fetch-error-toast';
import { useInScopeProductsByCustomer } from '@/hooks/use-in-scope-products';
import { useRecentProductsByCustomer } from '@/hooks/use-recent-products';
import { customersApi, productsApi, serviceRolesApi } from '@/lib/api';
import { SHOW_OUT_OF_SCOPE } from '@/lib/out-of-scope';
import { queryKeys } from '@/lib/query-keys';
import { useTimers } from '@/lib/timer-hooks';
import { cn } from '@/lib/utils';
import { warnIfTruncated } from '@/lib/warn-if-truncated';
import { ConfirmDialog } from './confirm-dialog';
import { CustomerAutocomplete } from './customer-autocomplete';
import { DiscardChangesDialog } from './discard-changes-dialog';
import { OutOfScopeBadge } from './out-of-scope-badge';
import { ProductAutocomplete } from './product-autocomplete';
import { ServiceRoleChip } from './product-label';
import { PulsingDot } from './pulsing-dot';
import { RecallEntryButton } from './recall-entry-button';
import { MOD_ENTER_KEYS, MOD_KEYSHORTCUT, ShortcutHint } from './shortcut-hint';
import { isActivityVisibleForCustomer } from './timesheet-activity-visibility';
import type { CellEditorValue } from './timesheet-cell-editor';
import { CellEntryEditor, EntryStatusDot } from './timesheet-cell-entries';
import { TimesheetEngagementCell } from './timesheet-engagement-cell';
import { TimesheetEngagementDialog } from './timesheet-engagement-dialog';
import { TimesheetLabelButton } from './timesheet-label-button';
import { useTimesheetMutations } from './timesheet-mutations';
import {
  engagementCandidatesForRow,
  engagementsActiveForRow,
  resolveRowEngagement,
} from './timesheet-row-engagement';
import type { TimesheetSubmitFieldErrors } from './timesheet-submit-errors';
import {
  addDays,
  buildManualLineRows,
  buildServerRows,
  claimedEntryIds,
  computeWeekFindMatches,
  computeWeekFindStatusCounts,
  deltaTextClass,
  type FindMatchCell,
  formatDelta,
  formatHours,
  isClearableEmptyEntry,
  isEmptyClearableRow,
  isEntryLocked,
  longDayName,
  type ManualLineState,
  normalizeIndex,
  type RowKey,
  rowEntries as rowEntriesOf,
  STANDARD_HOURS_PER_DAY,
  STANDARD_HOURS_PER_WEEK,
  shortDayName,
  stickyBillable,
  sumHours,
  type WeekRow,
  weekDates,
} from './timesheet-week';
import { TimesheetWeekGridEditSlot } from './timesheet-week-grid-edit-slot';

/**
 * Module-level singleton so `rolesForRow` and friends keep a stable
 * reference for the "no customer / no membership covers" branch.
 * Without it every render would feed ProductAutocomplete a fresh `[]`
 * and re-invalidate its `effectiveRoleIds` memo.
 */
const EMPTY_ROLE_IDS: readonly string[] = Object.freeze([]);

/** Stable empty status scope so the find-match memo sees the same reference
 *  when the caller passes no `findStatuses` — avoids re-running on every render. */
const EMPTY_STATUS_SET: ReadonlySet<TimesheetStatus> = new Set();

/**
 * Pull the whole (small, per-tenant) service-role catalog in one page so
 * the row label can resolve each product's primary role to its short code.
 * Mirrors the ProductAutocomplete query exactly — same shape, same query
 * key — so the two share a cache entry.
 */
const SERVICE_ROLES_LIST_QUERY = {
  sortField: 'name',
  sortDirection: 'asc',
  limit: 500,
} as const;

/**
 * The "Out of scope" badge on timesheet entries — off until out-of-scope
 * labelling is released. One switch for every surface: `SHOW_OUT_OF_SCOPE`.
 */
const SHOW_OUT_OF_SCOPE_BADGE = SHOW_OUT_OF_SCOPE;

/** Stable empty list for rows whose engagement picker can't open — a fresh
 *  `[]` every render would defeat any downstream identity comparison. */
const EMPTY_ENGAGEMENTS: readonly EngagementCandidate[] = Object.freeze([]);

// Tooltip on a row whose customer/activity is frozen because it holds a
// submitted/approved or period-locked entry. Without it the read-only label
// gives no reason for the missing edit affordance — this restores the guidance
// the old "change them from their cell" toast used to provide, now up front.
const LOCKED_ROW_REBIND_HINT =
  'This row has submitted or locked entries — edit them from their day cells.';

/** Which row attribute the grid is sorted by. `customer` sorts on the
 *  resolved customer name; `activity` on the activity (product) NAME only —
 *  the leading service-role chip is intentionally excluded from the sort key
 *  so two activities sharing a name order together regardless of role. */
type SortField = 'customer' | 'activity';
type SortDirection = 'asc' | 'desc';
type SortState = { field: SortField; direction: SortDirection };

/** Clickable column header that toggles the grid sort. Mirrors the timesheet
 *  report's header affordance (arrow up/asc, down/desc, faint two-way when
 *  inactive) so the two surfaces feel the same. */
function SortHeader({
  label,
  field,
  sort,
  onToggle,
}: {
  label: string;
  field: SortField;
  sort: SortState | null;
  onToggle: (field: SortField) => void;
}) {
  const active = sort?.field === field;
  return (
    <button
      type="button"
      className="inline-flex select-none items-center gap-1 hover:text-foreground"
      onClick={() => onToggle(field)}
    >
      {label}
      {active && sort?.direction === 'asc' ? (
        <ArrowUp className="size-3" />
      ) : active && sort?.direction === 'desc' ? (
        <ArrowDown className="size-3" />
      ) : (
        <ArrowUpDown className="size-3 opacity-40" />
      )}
    </button>
  );
}

/** Wrap each case-insensitive occurrence of `query` in `text` with a
 *  browser-find-style highlight. Empty / non-matching query → the text
 *  unchanged. Used on the customer + activity labels during a find so the user
 *  sees exactly what matched. */
function HighlightedText({ text, query }: { text: string; query: string }) {
  const q = query.trim();
  if (!q) return <>{text}</>;
  const lower = text.toLowerCase();
  const ql = q.toLowerCase();
  let from = lower.indexOf(ql);
  if (from === -1) return <>{text}</>;
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  while (from !== -1) {
    if (from > cursor) nodes.push(text.slice(cursor, from));
    nodes.push(
      <mark
        key={key++}
        className="rounded-[2px] bg-amber-200 px-0.5 text-amber-950 dark:bg-amber-500/40 dark:text-amber-50"
      >
        {text.slice(from, from + q.length)}
      </mark>,
    );
    cursor = from + q.length;
    from = lower.indexOf(ql, cursor);
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return <>{nodes}</>;
}

export interface TimesheetWeekGridHandle {
  addRow: () => void;
  /** True when the grid holds a started-but-unsaved draft row: a row where the
   *  user picked a customer/activity but entered no hours. Such a draft only
   *  persists once it gets hours (then it's retired into a server row), so it's
   *  silently lost on navigation away — the page reads this to prompt before
   *  leaving. A fully blank draft (nothing picked yet) carries no user effort,
   *  so it doesn't count. Read live (e.g. inside a route-guard predicate). */
  hasUnsavedDraftRows: () => boolean;
  /** Scroll the row holding `entryId` into view (e.g. a row just produced by
   *  committing a timer). Returns false when no rendered row owns that id — the
   *  caller retries after the next render once the refetch lands it. */
  scrollEntryIntoView: (entryId: string) => boolean;
  /** How many cells match the current find inputs (`findQuery` + `findStatuses`),
   *  in visual order. The find bar renders "N of M" from this. Read live. */
  getMatchCount: () => number;
  /** Open the find match at `index` (clamped into range): scroll its row into
   *  view and open that cell's editor popover. No-op when there are no matches.
   *  Returns the entry id opened, or null. */
  openMatchAt: (index: number) => string | null;
  /** Delete every clearable-empty draft CELL this week — any entry with no
   *  logged hours (null / 0 / "0.00") on an unlocked date (see
   *  `isClearableEmptyEntry`), whether or not its row also carries real hours.
   *  A manual line whose every cell is swept drops its now-empty scaffold too;
   *  a partially-swept line stays and releases just the cleared claims. Leaves
   *  any hours-bearing, locked, or submitted/approved/rejected/failed entry
   *  untouched. Toasts the count. The toolbar's "Clear empty entries" button
   *  drives this. */
  clearEmptyEntries: () => void;
}

interface Props {
  user: User;
  weekStart: string;
  entries: Timesheet[];
  loading: boolean;
  /** Customers the target user is on the team of in this week (ALL statuses),
   *  pre-fetched by the page route. Used to resolve the label + isInternal of
   *  a row's bound customer — including one that was deactivated after the row
   *  was logged, since deactivation doesn't end team membership. NOT the list
   *  the dropdown offers; see `selectableCustomers`. */
  teamCustomers: readonly Customer[];
  /** The subset of `teamCustomers` the dropdown may OFFER — active only,
   *  filtered server-side by the page route. Feeds the per-row
   *  CustomerAutocomplete `items` so a deactivated customer can never be
   *  picked, while `teamCustomers` still renders an already-bound one. */
  selectableCustomers: readonly Customer[];
  /** Per-day predicate driving the cell-disable rule: even if the customer
   *  is on the target's team somewhere in the week, only the specific days
   *  inside the membership window are editable. */
  isOnTeamForDay: (customerId: string, date: string) => boolean;
  /** All customer-team memberships for the target user that overlap the
   *  visible week. Pre-fetched by the page route alongside `teamCustomers`
   *  and `isOnTeamForDay`. The grid walks these to derive the per-row,
   *  per-customer role set passed to the product picker — see
   *  `rolesForRow` for the resolution semantics. */
  memberships: readonly CustomerTeamMember[];
  /** Every engagement (SOW) version covering the visible week for the target
   *  user's team customers, with the activity ids each lists. One request per
   *  week (see `useEngagements`); the grid filters per row. */
  engagementVersions: readonly EngagementVersion[];
  /** `true` once the engagement request has SETTLED. Until then a row must
   *  not be told "no engagement covers this" — an in-flight empty list is
   *  indistinguishable from a real zero, and the grid would flash
   *  "No engagement" on every row before the data lands. */
  engagementsLoaded: boolean;
  /** Field errors raised by the most recent submit attempt — keyed by
   *  timesheet id. The grid highlights the offending cells inline (red
   *  border + tooltip). Self-heals as the user fixes the values (the
   *  page route prunes ids as `entries` change). */
  submitFieldErrors?: TimesheetSubmitFieldErrors;
  /** The page header owns the "+ Add row" button so it sits next to the
   *  week pager. The grid exposes its addRow function through this ref. */
  /** Whether the period containing `date` has closed for `customerId`
   *  (null = internal/unbound). An active admin unlock covering that scope
   *  re-opens it. Mirrors the backend lock gate; the server is the backstop.
   *
   *  `stage` selects WHICH of the period's two boundaries to ask about, and
   *  omitting it means the earlier, stricter `submission` one. Callers acting
   *  on an existing row must pass the row's own stage
   *  (`timesheetLockStageFor('edited', status)`) or a `rejected`/`failed` row
   *  will read as locked through settlement while the API still accepts the
   *  correction. It is a declared parameter rather than an implicit extra
   *  argument so an implementation that ignores it is a type error here
   *  instead of a silently stricter UI. */
  isDateLocked: (date: string, customerId: string | null, stage?: TimesheetLockStage) => boolean;
  /** Free-text find query (customer OR activity name). When `findActive` and
   *  this or `findStatuses` is non-empty, matching rows stay lit with their
   *  matched name substring highlighted and non-matching rows dim. */
  findQuery?: string;
  /** Status scope for the find. Empty = all statuses. Combined with `findQuery`
   *  as an intersection (row text AND cell status). */
  findStatuses?: ReadonlySet<TimesheetStatus>;
  /** Whether a find session is open. Drives the dim/highlight treatment; when
   *  false the grid renders normally regardless of the two inputs above. */
  findActive?: boolean;
  /** Fired with the match count whenever it changes, so the find bar can show a
   *  live "N of M" without reading the grid's ref during render (which lags a
   *  keystroke). */
  onFindMatchCountChange?: (count: number) => void;
  /** Fired with the count of clearable-empty draft cells (each an unlocked
   *  no-hours draft entry, regardless of its row's other cells) whenever it
   *  changes, so the toolbar's "Clear empty entries" button can label itself and
   *  disable when there's nothing to clear — the cell grouping lives here, not on
   *  the page's flat entry list, so the count is computed and pushed up (mirrors
   *  `onFindMatchCountChange`). */
  onEmptyEntryCountChange?: (count: number) => void;
  /** Fired with the per-status entry counts (scoped to the current `findQuery`
   *  text, ignoring the status scope) whenever they change, so the find bar's
   *  status dots reflect the text-filtered subset and drop to zero — hidden —
   *  as the query narrows. Names resolve here (batched label maps), so the
   *  count can't be computed from the page's raw entries alone. */
  onFindStatusCountsChange?: (counts: Record<TimesheetStatus, number>) => void;
  ref?: React.Ref<TimesheetWeekGridHandle>;
}

/** A grid row over the `Timesheet` client type. Construction + ordering live
 *  in `./timesheet-week` (pure + unit-tested); the grid only consumes them. */
type RowData = WeekRow<Timesheet>;

/** Row context a day cell hands to the shared, row-agnostic create handler.
 *  All primitives, so the cell can carry them as `memo`-stable props. */
interface CellCreateContext {
  customerId: string | null;
  productId: string | null;
  /** The engagement the row resolved to (bound or picked), so a new entry is
   *  born attributed instead of being patched a moment later. */
  engagementId: string | null;
  lineKey: RowKey;
  isManualLine: boolean;
  dayIndex: number;
  date: string;
}

/**
 * Inline-edit week grid. One row per (customer, product) pair; columns are
 * Mon–Sun + Total. Click any cell → popover for hours/description/billable.
 * Auto-saves; clearing hours deletes the entry. `addRow` (exposed via the
 * `ref` prop and consumed by the page header) inserts a blank draft row
 * whose cells become enabled once both customer and product are chosen.
 */
export function TimesheetWeekGrid({
  user,
  weekStart,
  entries,
  loading,
  teamCustomers,
  selectableCustomers,
  isOnTeamForDay,
  memberships,
  engagementVersions,
  engagementsLoaded,
  submitFieldErrors,
  isDateLocked,
  findQuery = '',
  findStatuses,
  findActive = false,
  onFindMatchCountChange,
  onFindStatusCountsChange,
  onEmptyEntryCountChange,
  ref,
}: Props) {
  const dates = useMemo(() => weekDates(weekStart), [weekStart]);
  // Every day in the week locked → a new row would only yield read-only
  // cells. Gates the empty-state "Add row" affordance and guards `addRow`
  // itself, mirroring the page-header toolbar. `null` customer matches a
  // not-yet-bound new row (an all-customer unlock still re-opens it).
  const weekFullyLocked = useMemo(
    () => dates.every((d) => isDateLocked(d, null)),
    [dates, isDateLocked],
  );

  // Manual lines: rows the user added via "Add row" and chose to keep SEPARATE
  // for this editing session, even when they duplicate an existing row's
  // (customer, product). A line starts as an empty scaffold (no claimed
  // entries) and, unlike the old auto-retiring draft, PERSISTS once it produces
  // an entry — owning that entry id so `buildServerRows` won't pack it back into
  // the matching (customer, product) row. The state is per-week and transient
  // (reset on week change / reload below), so a refresh re-derives everything
  // from flat entries and the duplicates merge — the accepted trade-off for not
  // persisting a line identity on the entry. See `ManualLineState`.
  const [manualLines, setManualLines] = useState<ManualLineState[]>([]);

  // Live entry lookup so a manual line can resolve each claimed id to its
  // current row (the id may have been swapped temp→real or deleted since claim).
  const entriesById = useMemo(() => {
    const m = new Map<string, Timesheet>();
    for (const e of entries) m.set(e.id, e);
    return m;
  }, [entries]);

  // Entry ids owned by a manual line — excluded from auto-grouping so each is
  // rendered ONLY by its line, never also packed into a (customer, product) row.
  // Reduced to a stable content key (sorted, joined) so the `serverRows` regroup
  // below only re-runs when the SET of claimed ids actually changes — not on
  // every manual-line tweak (adding an empty row, picking a customer) that mints
  // a fresh `manualLines` array but claims nothing new.
  const claimedIdsKey = useMemo(
    () => [...claimedEntryIds(manualLines)].sort().join('|'),
    [manualLines],
  );

  const serverRows = useMemo(() => {
    if (claimedIdsKey === '') return buildServerRows(entries, weekStart);
    const claimed = new Set(claimedIdsKey.split('|'));
    return buildServerRows(
      entries.filter((e) => !claimed.has(e.id)),
      weekStart,
    );
  }, [entries, weekStart, claimedIdsKey]);

  // The manually-separated rows, with their claimed entries resolved live.
  const manualRows = useMemo(
    () => buildManualLineRows(manualLines, entriesById),
    [manualLines, entriesById],
  );

  // Set of manual-line keys, so the row handlers can tell a manual line from an
  // auto-packed server row (they share the `RowData` shape but behave
  // differently on create / rebind / trash).
  const manualLineKeys = useMemo(() => new Set(manualLines.map((l) => l.key)), [manualLines]);

  // Stable array identity when neither source changed, so the customerIds /
  // productIds / dailyTotals memos (keyed on `rows`) can cache across
  // re-renders that don't touch the data — optimistic flag flips, popover
  // open/close, hover. (A successful range refetch DOES return a fresh
  // `entries` array, which re-derives `serverRows` and busts this anyway —
  // this memo doesn't save the poll, just the data-unchanged re-renders.)
  const rows: RowData[] = useMemo(() => [...serverRows, ...manualRows], [serverRows, manualRows]);

  // How many individual cells are clearable-empty drafts (null / 0 / "0.00" on
  // an unlocked date) — the placeholder clutter "Copy last week" leaves behind,
  // whether or not the cell's row also carries real hours. Pushed up so the
  // toolbar's "Clear empty entries" button can label/disable itself; the cell
  // grouping only exists here, not on the page's flat entry list. Locked blank
  // drafts are excluded: the server would reject their DELETE, so counting them
  // would over-promise (and `clearEmptyEntries` uses the same predicate, so the
  // count and the sweep can't disagree).
  const emptyEntryCount = useMemo(() => {
    const isLocked = (e: Timesheet) => isEntryLocked(e, isDateLocked);
    return rows.reduce(
      (n, r) => n + rowEntriesOf(r).filter((e) => isClearableEmptyEntry(e, { isLocked })).length,
      0,
    );
  }, [rows, isDateLocked]);
  useEffect(() => {
    onEmptyEntryCountChange?.(emptyEntryCount);
  }, [emptyEntryCount, onEmptyEntryCountChange]);

  // Optional column sort. `null` = the natural (createdAt) order from
  // `buildServerRows`; a click on the Customer / Activity header sets it.
  // Toggling the same header flips direction; switching headers resets to asc.
  const [sort, setSort] = useState<SortState | null>(null);
  const toggleSort = useCallback((field: SortField) => {
    setSort((prev) =>
      prev?.field === field
        ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { field, direction: 'asc' },
    );
  }, []);

  // Sibling entries indexed by (customer, product, date), so a NEW entry in an
  // empty cell can inherit `billable` from the day's most-recent same-pair
  // entry (stickyBillable). With one entry per cell the editor's own cell is
  // empty, so the sticky source lives on the OTHER duplicate row's cell — hence
  // an index over all entries rather than the cell's own. Mirrors the day view.
  const siblingsByCell = useMemo(() => {
    const m = new Map<string, Timesheet[]>();
    for (const e of entries) {
      const k = `${e.customerId ?? ''}|${e.productId ?? ''}|${e.date}`;
      const arr = m.get(k);
      if (arr) arr.push(e);
      else m.set(k, [e]);
    }
    return m;
  }, [entries]);

  // One batched call each for customer + product labels — keeps the row
  // labels readable without firing N parallel detail GETs as the grid grows.
  const customerIds = useMemo(
    () => Array.from(new Set(rows.map((r) => r.customerId).filter((v): v is string => v !== null))),
    [rows],
  );
  const customersQuery = useQuery({
    queryKey: queryKeys.customers.byIds(customerIds),
    queryFn: () => customersApi.findByIds(customerIds),
    enabled: customerIds.length > 0,
    staleTime: 60_000,
    // Picking a new customer anywhere in the grid adds its id to `customerIds`,
    // which is a fresh cache key with no entry yet. Without this, `data`
    // would reset to `undefined` for that one round trip, collapsing
    // `customerById` to empty and flashing every OTHER row's already-resolved
    // customer name to the "…" placeholder. Keeping the previous page's data
    // in view during that window means only the newly-picked row is ever
    // missing from the map until the batch lands.
    placeholderData: keepPreviousData,
  });
  const customerById = useMemo(() => {
    const m = new Map<string, Customer>();
    for (const c of customersQuery.data?.data ?? []) m.set(c.id, c);
    return m;
  }, [customersQuery.data]);

  const productIds = useMemo(
    () => Array.from(new Set(rows.map((r) => r.productId).filter((v): v is string => v !== null))),
    [rows],
  );
  const productsQuery = useQuery({
    queryKey: queryKeys.products.byIds(productIds),
    queryFn: () => productsApi.findByIds(productIds),
    enabled: productIds.length > 0,
    staleTime: 60_000,
    // Picking a new activity anywhere in the grid adds its id to `productIds`,
    // which is a fresh cache key with no entry yet. Without this, `data`
    // would reset to `undefined` for that one round trip, collapsing
    // `productById` to empty and flashing every OTHER row's already-resolved
    // activity name to the "…" placeholder. Keeping the previous page's data
    // in view during that window means only the newly-picked row is ever
    // missing from the map — and that one is covered separately by
    // `pickedProductsRef`.
    placeholderData: keepPreviousData,
  });
  const productById = useMemo(() => {
    const m = new Map<string, Product>();
    for (const p of productsQuery.data?.data ?? []) m.set(p.id, p);
    return m;
  }, [productsQuery.data]);

  // Products the user just picked in the activity dropdown, cached by id so
  // the per-day cell gate can read the activity's full role set
  // ({primary} ∪ visibleTo) the instant it's chosen. The batched
  // `productsApi.findByIds` query above lags a fresh draft pick by a round
  // trip, and without the visibleTo set the gate would wrongly disable cells
  // for a shared activity whose primary role the user doesn't hold (e.g. an
  // internal activity, primary to the Internal role but visible to every real
  // role). A ref (not state) — it only needs to be readable during the same
  // render that `handleProductChange` triggers via `updateManualLine`.
  const pickedProductsRef = useRef(new Map<string, Product>());

  // Render order. Without a sort this is just `rows` (natural createdAt order).
  // With one, only the saved server rows are reordered by the resolved
  // customer / activity NAME — draft rows stay pinned to the bottom in add
  // order so a freshly added row the user is filling in never jumps around.
  // The Array.sort is stable, so rows with an equal key keep their createdAt
  // order. Names resolve asynchronously; a missing name sorts as '' until its
  // batched label query lands, then the memo re-sorts.
  const displayRows = useMemo(() => {
    if (!sort) return rows;
    const dir = sort.direction === 'asc' ? 1 : -1;
    const keyFor = (r: RowData) =>
      sort.field === 'customer'
        ? r.customerId
          ? (customerById.get(r.customerId)?.name ?? '')
          : ''
        : r.productId
          ? (productById.get(r.productId)?.name ?? '')
          : '';
    const drafts = rows.filter((r) => r.isDraft);
    const sorted = rows
      .filter((r) => !r.isDraft)
      .sort((a, b) => keyFor(a).localeCompare(keyFor(b), undefined, { sensitivity: 'base' }) * dir);
    return [...sorted, ...drafts];
  }, [rows, sort, customerById, productById]);

  // Resolve a row's customer / activity display name — the same strings the
  // labels render and the find text matches against. Shared by the find-match
  // computation and the in-label highlight so "what matched" can't drift from
  // "what's highlighted".
  const customerNameOf = useCallback(
    (customerId: string | null) => (customerId ? (customerById.get(customerId)?.name ?? '') : ''),
    [customerById],
  );
  const activityNameOf = useCallback(
    (productId: string | null) => (productId ? (productById.get(productId)?.name ?? '') : ''),
    [productById],
  );

  const findHasQuery = findActive && findQuery.trim().length > 0;

  // Stable signature of the customer/activity names that feed TEXT matching.
  // The batched label queries refetch on a 60s staleTime and hand back new map
  // references with identical content, which would otherwise churn the resolver
  // identities and force a needless match recompute. Only built when a text
  // query is active (status-only find never reads names); computeWeekFindMatches
  // reads exactly these names, so any change that could alter a match also
  // changes this signature. ␟ / ␁ are non-printing field/record separators
  // so distinct name pairs can't collide.
  const findNameSig = useMemo(() => {
    if (!findHasQuery) return '';
    let sig = '';
    for (const r of displayRows) {
      sig += `${customerNameOf(r.customerId)}␟${activityNameOf(r.productId)}␁`;
    }
    return sig;
  }, [findHasQuery, displayRows, customerNameOf, activityNameOf]);

  // Ordered find matches over the CURRENTLY DISPLAYED rows (so prev/next steps
  // in visual order and follows the active sort). Only computed while a find
  // session is open; otherwise the empty array keeps the dim/highlight off.
  // Keyed on `findNameSig` rather than the resolver identities so an identical
  // label refetch is a no-op (see above); displayRows/query/statuses still
  // recompute as needed. The resolvers are read fresh from the enclosing scope.
  const findMatches = useMemo<FindMatchCell[]>(() => {
    if (!findActive) return [];
    return computeWeekFindMatches(displayRows, {
      query: findQuery,
      statuses: findStatuses ?? EMPTY_STATUS_SET,
      customerName: customerNameOf,
      activityName: activityNameOf,
    });
    // customerNameOf/activityNameOf are deliberately NOT deps — they change
    // identity on every label refetch; `findNameSig` captures their relevant
    // output and is stable across identical-content refetches. The memo reads
    // the latest resolvers from scope when it does re-run.
  }, [findActive, displayRows, findQuery, findStatuses, findNameSig]);

  // Row keys that contain at least one match — rows NOT in this set dim while a
  // find is active. Empty when the find has no active inputs, so nothing dims.
  const matchingRowKeys = useMemo(() => {
    const s = new Set<RowKey>();
    for (const m of findMatches) s.add(m.rowKey);
    return s;
  }, [findMatches]);
  // The find is actively narrowing (has a query and/or a status scope), so
  // non-matching rows should dim. Without inputs the find is open but idle —
  // nothing dims.
  const findNarrowing = findHasQuery || (findActive && (findStatuses?.size ?? 0) > 0);
  // The substring to highlight in the row labels — only when a text query is
  // active (a status-only find dims non-matches but has no text to mark).
  const findHighlight = findHasQuery ? findQuery : '';

  // Latest matches read through a ref so `openMatchAt` can stay a stable
  // (empty-dep) callback — the imperative handle then never churns as the find
  // inputs / data change, mirroring `scrollEntryIntoView`'s `rowsRef` trick.
  const findMatchesRef = useRef(findMatches);
  findMatchesRef.current = findMatches;

  // Push the live match count to the parent (the find bar) so its "N of M"
  // never reads a stale ref during render. Keyed on the count only, so it
  // doesn't fire when the match set is reordered but the size is unchanged.
  const findMatchCount = findMatches.length;
  useEffect(() => {
    onFindMatchCountChange?.(findMatchCount);
  }, [findMatchCount, onFindMatchCountChange]);

  // Per-status entry counts for the find bar's dots, scoped to the current TEXT
  // query (not the status selection — each dot stays an independent toggle).
  // Computed here because it needs the same batched name resolvers as the match
  // set; pushed up so the bar reflects the text-filtered subset and hides a dot
  // that the query has emptied. Keyed on `findNameSig` (stable across identical
  // label refetches) for the same reason `findMatches` is; the resolvers are
  // read fresh from scope. `serializedFindStatusCounts` gates the push so it
  // fires only when a count actually changes, not on every rows/query identity.
  const findStatusCounts = useMemo(
    () =>
      computeWeekFindStatusCounts(displayRows, {
        query: findQuery,
        customerName: customerNameOf,
        activityName: activityNameOf,
      }),
    // Same deliberate-omission of the resolvers as `findMatches` above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayRows, findQuery, findNameSig],
  );
  const serializedFindStatusCounts = TIMESHEET_STATUSES.map((s) => findStatusCounts[s]).join(',');
  useEffect(() => {
    onFindStatusCountsChange?.(findStatusCounts);
    // findStatusCounts is a fresh object each render; the serialized values are
    // the real change signal, so we key on them (not the object identity).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serializedFindStatusCounts, onFindStatusCountsChange]);

  // Service-role catalog → short-code lookup, so the closed row label can
  // prefix the activity name with its role chip (same disambiguation the
  // ProductAutocomplete dropdown provides). Small, bounded per tenant and
  // cache-shared with the picker via the same query key, so this is a
  // cache hit once the picker has been opened.
  const serviceRolesQuery = useQuery({
    queryKey: queryKeys.serviceRoles.list(SERVICE_ROLES_LIST_QUERY),
    queryFn: () => serviceRolesApi.list(SERVICE_ROLES_LIST_QUERY),
  });
  warnIfTruncated(serviceRolesQuery.data, 'timesheet-week-grid role badges');
  const serviceRoleCodeById = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of serviceRolesQuery.data?.data ?? []) m.set(r.id, r.shortCode);
    return m;
  }, [serviceRolesQuery.data]);

  // Single batched query — one round trip even for users on many team
  // customers. `isLoaded` distinguishes "fetched, no SOW" from "still
  // fetching"; we pass `undefined` to the picker until loaded so it
  // doesn't section everything as out-of-scope mid-flight.
  const weekEnd = addDays(weekStart, 6);
  const { byCustomer: inScopeByCustomer, isLoaded: inScopeLoaded } = useInScopeProductsByCustomer(
    user.id,
    weekStart,
    weekEnd,
  );

  // "Used before by you" recents for the picker's Recent band — same
  // one-round-trip, server-derived-customer-set shape as in-scope. No
  // `isLoaded` gate: recents are a soft ordering hint, so the picker renders
  // immediately and the band fills in when the query settles.
  const { byCustomer: recentByCustomer } = useRecentProductsByCustomer(user.id, weekStart, weekEnd);

  // One toast per failed label fetch — both query errors share the same
  // shape, so route them through the shared hook instead of N copies.
  useFetchErrorToast(customersQuery.error, 'customer names');
  useFetchErrorToast(productsQuery.error, 'activity names');

  // Per-customer membership index. Built once per (memberships-content,
  // dates) so the inner scan in `rolesByCustomer` only walks the rows
  // for the active customer, not the whole array. Without this the
  // O(uniqueCustomers × days × all-memberships) cost would erase the
  // per-customer locality the prior PR's review flagged.
  //
  // Keyed on `contentKey` (NOT the `memberships` array reference) so a
  // React Query refetch with byte-identical content keeps the index
  // warm — otherwise every 60 s refetch / focus / reconnect would
  // rebuild the index and bust downstream memos.
  const contentKey = useMemo(() => membershipsContentKey(memberships), [memberships]);

  const membershipsByCustomer = useMemo(() => {
    const m = new Map<string, CustomerTeamMember[]>();
    for (const mem of memberships) {
      const arr = m.get(mem.customerId);
      if (arr) arr.push(mem);
      else m.set(mem.customerId, [mem]);
    }
    return m;
    // is the content-stable identity for `memberships` — using it here
    // lets the index survive same-content refetches.
  }, [contentKey]);

  // Pre-compute the per-customer role set ONCE per (memberships-content,
  // dates) change. The picker prop `filterServiceRoleIds={rolesForRow(...)}`
  // is invoked inline on every render, so returning a fresh array each
  // call would invalidate ProductAutocomplete's `effectiveRoleIds`/
  // `roleIdSet`/`items` memos every render. With this Map the helper
  // becomes a stable lookup that returns the same array reference until
  // inputs change.
  //
  // For each customer we walk the 7 days, scanning only that customer's
  // pre-grouped slice (`candidates`). Multi-role: a user may hold SEVERAL
  // roles covering the same day, and a second role may start partway through
  // the week — so for each day we add EVERY covering membership's role, not
  // just the first match. (The old `find`-first-match + `cached` fast-path
  // returned only one role per customer, silently dropping a user's other
  // roles.) Day-list and timer-popover use the shared `findCoveringMemberships`
  // helper for the same union. Cost is 7 × (memberships on that customer),
  // which is tiny.
  const rolesByCustomer = useMemo(() => {
    const out = new Map<string, readonly string[]>();
    for (const [customerId, candidates] of membershipsByCustomer) {
      out.set(customerId, unionRolesCoveringDates(candidates, dates));
    }
    return out;
  }, [membershipsByCustomer, dates]);

  // The row's activity scope is ALWAYS the covering customer-team
  // membership role(s) — customer_team_members is the source of truth, the
  // same rule the server enforces on write. Both branches return the
  // module-level frozen `EMPTY_ROLE_IDS` so the picker's `effectiveRoleIds`
  // memo sees a stable reference for the "no customer" / "no covering
  // membership" cases. An empty result means the role is undeterminable, so
  // the caller disables the activity picker rather than letting an empty
  // filter fall through to the full catalog (see `rowHasMembershipRole`).
  const rolesForRow = useCallback(
    (customerId: string | null): readonly string[] => {
      if (!customerId) return EMPTY_ROLE_IDS;
      return rolesByCustomer.get(customerId) ?? EMPTY_ROLE_IDS;
    },
    [rolesByCustomer],
  );

  // Can this row log on `date`? The activity is loggable under any role in its
  // {primary} ∪ visibleTo set — the same access rule the server enforces in
  // resolveEntryRole — so a cell is editable when the user holds ANY covering
  // membership on that date whose role is in that set, NOT only the activity's
  // pinned primary role. This matters for shared activities whose primary role
  // the user never holds: internal activities are primary to the "Internal"
  // role but visible to every real role, so an Ops user (who only ever holds
  // an Ops membership on the internal customer) must still be able to log
  // against them. The per-day shape also still handles a role added mid-week —
  // a membership that only covers part of the week gates the days it doesn't.
  // Until the product detail loads we fall back to the pinned-role check.
  const activityCoversDate = useCallback(
    (
      customerId: string | null,
      product: Product | null | undefined,
      pinnedRoleId: string | null,
      date: string,
    ): boolean => {
      if (!customerId) return true;
      if (!product && !pinnedRoleId) return true; // nothing pinned yet → don't gate
      const candidates = membershipsByCustomer.get(customerId);
      if (!candidates) return false;
      const allowed = product
        ? new Set<string>([product.serviceRoleId, ...(product.visibleToServiceRoleIds ?? [])])
        : new Set<string>([pinnedRoleId as string]);
      return candidates.some((m) => allowed.has(m.serviceRoleId) && coversDate(m, date));
    },
    [membershipsByCustomer],
  );

  // Centralized optimistic mutations — see timesheet-mutations.tsx. The raw
  // mutations stay exposed for `isPending`/delete.
  const { deleteMutation, createEntry, patchEntry, deleteEntry, recallEntry, promptHost } =
    useTimesheetMutations(user, weekStart);

  // Active-timer match used to paint the pulsing dot on the matching cell.
  // The hook polls every 5s; cheap query.
  const { running: runningTimer } = useTimers();
  // Tracked but no longer used to block cells — optimistic updates mean the
  // grid is already consistent with what the user sees, so a save in flight
  // shouldn't gate the next interaction. Still useful for the per-row delete.
  const deleting = deleteMutation.isPending;

  // Singleton-edit state: only the active cell mounts an autocomplete; every
  // other row renders a static label. Avoids N component instances + N
  // detail fetches per render.
  const [editing, setEditing] = useState<{
    rowKey: RowKey;
    field: 'customer' | 'product' | 'engagement';
  } | null>(null);

  // The row whose engagement is being ASKED for, in a dialog. Deliberately not
  // folded into `editing`: that state drives the inline pickers (the CHANGE
  // path), and the two must be able to open and close independently — skipping
  // the dialog must not leave an inline dropdown open on the row behind it.
  const [engagementPrompt, setEngagementPrompt] = useState<RowKey | null>(null);
  // The activity pick the open prompt is ABOUT, when it was raised by one. The
  // pick is held here rather than saved: the server refuses an entry several
  // engagements cover until one is chosen, so the activity and its engagement
  // are saved together on the answer — and Cancel simply never saves the pick.
  const [pendingActivity, setPendingActivity] = useState<{
    rowKey: RowKey;
    product: Product | null;
  } | null>(null);

  // A customer pick that would DELETE the row's draft hours (the new
  // customer can't perform the row's activity), pending the user's OK.
  // Deliberately not folded into `editing` for the same reason as
  // `engagementPrompt`: the picker (CHANGE path) already closed by the time
  // this opens, and canceling here must not reopen it — the label simply
  // keeps showing the old customer, since `row.customerId` never changed.
  const [confirmCustomerChange, setConfirmCustomerChange] = useState<{
    rowKey: RowKey;
    id: string | null;
    newCustomerName: string;
  } | null>(null);

  // Rows can arrive ALREADY ambiguous — loaded from the server, or created by
  // "Copy last week" — and those never went through an activity pick, so
  // nothing prompted for them. Submit still rejects them, which left the user
  // with N inline errors and no way to answer except hunting each row.
  //
  // So the failed submit itself opens the first offending row's dialog. Keyed
  // on the SET of flagged entry ids, not on the map identity: the page mints a
  // fresh `submitFieldErrors` object as entries change, and re-opening the
  // dialog on every one of those would fight the user.
  const engagementErrorKey = useMemo(() => {
    if (submitFieldErrors == null) return '';
    return Object.entries(submitFieldErrors)
      .filter(([, f]) => f?.engagementId != null)
      .map(([id]) => id)
      .sort()
      .join('|');
  }, [submitFieldErrors]);
  const lastEngagementErrorKeyRef = useRef('');

  // Which cell's editor popover is open, lifted out of the per-cell `CellSlot`
  // so the find navigation (below) can open a specific cell programmatically.
  // Exactly one cell is open at a time. Keyed by `(rowKey, dayIndex)` — the
  // same identity a find match carries. A cell derives its own `open` from this
  // (a primitive boolean prop), so flipping it re-renders only the two cells
  // involved in a transition, not the whole grid (see the render-count test).
  const [openCell, setOpenCell] = useState<{ rowKey: RowKey; dayIndex: number } | null>(null);
  // Stable, identity-shared open toggler passed to every cell (mirrors the
  // onCellCreate/Patch/Delete pattern): the cell hands back its own row key +
  // day so no per-cell closure is minted, keeping `CellSlot` memo-stable.
  const onCellOpenChange = useCallback((rowKey: RowKey, dayIndex: number, next: boolean) => {
    setOpenCell(next ? { rowKey, dayIndex } : null);
  }, []);

  // Manual lines + the open editor are per-week, transient UI state. The grid is
  // not remounted across week navigation (the parent renders it without a
  // `key={weekStart}`), so without this reset a manual line added on one week
  // would persist into the next — showing a phantom row on a week where nothing
  // was ever created, and (worse) claiming ids from another week. Clearing it
  // here is also what makes a week-switch merge separated duplicates, mirroring
  // the merge-on-reload behaviour. `serverRows` already re-derives per week from
  // the range query; only this local state needs clearing. Keeps the column
  // sort intact (a navigation shouldn't drop the user's sort choice).
  useEffect(() => {
    setManualLines([]);
    setEditing(null);
  }, [weekStart]);

  // Open the first rejected row when a submit surfaces engagement errors. Runs
  // off the derived key, so it fires once per distinct set of flagged entries.
  useEffect(() => {
    if (engagementErrorKey === lastEngagementErrorKeyRef.current) return;
    lastEngagementErrorKeyRef.current = engagementErrorKey;
    if (engagementErrorKey === '') return;
    const flagged = new Set(engagementErrorKey.split('|'));
    const target = rows.find((r) => rowEntriesOf(r).some((e) => flagged.has(e.id)));
    if (target) setEngagementPrompt(target.key);
  }, [engagementErrorKey, rows]);

  // Add a blank draft row. Cells stay disabled until customer + product set.
  // `useCallback` with `[]` deps gives a stable identity — addRow only
  // touches stable setState references (via the updater form), so it never
  // needs to "see" newer closure state. That stability matters for the
  // useImperativeHandle below: with an unstable addRow the handle would
  // be recreated every render, churning the parent ref.
  const addRow = useCallback(() => {
    const key = `draft-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setManualLines((prev) => [
      ...prev,
      {
        key,
        customerId: null,
        productId: null,
        engagementId: null,
        roleId: null,
        claimedIdsByDay: Array.from({ length: 7 }, () => null),
      },
    ]);
    // Auto-open the customer picker on the new row so the user doesn't have
    // to click an extra "Pick customer…" placeholder.
    setEditing({ rowKey: key, field: 'customer' });
    // The new row renders at the bottom of a potentially-scrolled grid; flag it
    // so the effect below scrolls it into view once it's committed to the DOM.
    pendingScrollRowKeyRef.current = key;
  }, []);

  // Add a blanked line already pinned to `customerId` (no auto-opened picker).
  // Used when a customer change clears a GROUPED server row's activity: its
  // drafts are deleted, which would otherwise make the row vanish entirely — so
  // we leave a blank row on the new customer for the user to re-pick an activity
  // (a manual line, like "Add row" produces, already gets this for free).
  const addBlankLineForCustomer = (customerId: string) => {
    const key = `draft-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setManualLines((prev) => [
      ...prev,
      {
        key,
        customerId,
        productId: null,
        engagementId: null,
        roleId: null,
        claimedIdsByDay: Array.from({ length: 7 }, () => null),
      },
    ]);
  };

  // A started-but-empty draft (customer or activity picked, no hours on any
  // day) won't survive navigation — it only persists once it gets hours, which
  // retires it into a server row. Surface that so the page can prompt before
  // leaving. A draft never carries entries (it's retired the moment it does),
  // so the `entriesByDay` check is belt-and-suspenders, not load-bearing.
  const hasUnsavedDraftRows = useCallback(
    () =>
      manualLines.some(
        (l) =>
          (l.customerId !== null || l.productId !== null) &&
          l.claimedIdsByDay.every((id) => id == null),
      ),
    [manualLines],
  );

  // The bordered box wrapping the scrollable table — `scrollEntryIntoView`
  // queries within it for the row tagged with the committed entry's id.
  const gridContainerRef = useRef<HTMLDivElement>(null);
  // Latest rows read through a ref so `scrollEntryIntoView` can stay a stable
  // (empty-dep) callback — the handle then never churns on data refetches.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  // `addRow` appends the draft to the bottom of a grid the user may have
  // scrolled away from. setState is async, so we stash the new row's key and
  // scroll to it here — once the render that adds it has committed the row's
  // `data-row-key` element to the DOM.
  const pendingScrollRowKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = pendingScrollRowKeyRef.current;
    if (!key) return;
    const el = gridContainerRef.current?.querySelector<HTMLElement>(
      `[data-row-key="${CSS.escape(key)}"]`,
    );
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    pendingScrollRowKeyRef.current = null;
  }, [rows]);
  const scrollEntryIntoView = useCallback((entryId: string) => {
    // A committed entry lands in exactly one (customer, product, slot) row; find
    // it via the packed cells, then scroll that row by its rendered `data-row-key`.
    const row = rowsRef.current.find((r) => r.entriesByDay.some((e) => e?.id === entryId));
    if (!row) return false;
    const el = gridContainerRef.current?.querySelector<HTMLElement>(
      `[data-row-key="${CSS.escape(row.key)}"]`,
    );
    if (!el) return false;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  }, []);

  // Open the find match at `index`: set `openCell` (which opens that cell's
  // popover + paints its ring) and scroll its row into view. Reads matches +
  // rows through refs so the handle stays identity-stable across find-input and
  // data changes (like `scrollEntryIntoView`). Index is clamped so a stale index
  // after an edit shrank the list still lands on a valid match.
  const openMatchAt = useCallback((index: number): string | null => {
    const matches = findMatchesRef.current;
    if (matches.length === 0) return null;
    const match = matches[normalizeIndex(index, matches.length)];
    if (!match) return null;
    setOpenCell({ rowKey: match.rowKey, dayIndex: match.dayIndex });
    // Reveal the row; the popover itself autofocuses its first field on open.
    gridContainerRef.current
      ?.querySelector<HTMLElement>(`[data-row-key="${CSS.escape(match.rowKey)}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return match.entryId;
  }, []);
  const getMatchCount = useCallback(() => findMatchesRef.current.length, []);

  // Delete every wholly-empty row this week in one gesture (toolbar "Clear
  // empty rows"). Reads rows + handlers through refs so the callback — exposed
  // on the imperative handle — keeps a stable identity across data refetches.
  // Mirrors the per-row trash's manual-line bookkeeping: for a row that's
  // entirely clearable (every entry a blank/zero draft on an unlocked date), a
  // manual-line scaffold is dropped outright (that releases its claims), while a
  // server row just has its entries deleted; the optimistic cache pulls the row
  // from view.
  //
  // The summary toast is deferred to the deletes actually landing: each DELETE
  // is optimistic, so firing "Cleared N" up front would claim success even if a
  // delete then failed and rolled its row back. Instead we count settled
  // callbacks (success + error) and, once all N have settled, report the true
  // outcome: all-ok → "Cleared N"; partial → a "Cleared X of N; Y failed"
  // warning so the user isn't left thinking nothing landed when most did; all-
  // failed → nothing here (the delete mutation's own error toast already said
  // so). Those error toasts fire alongside in every failing case, deduplicated
  // by message — a sweep where every delete fails the same way shows ONE
  // toast, not one per entry, but two distinct failures still show both.
  const clearEmptyEntries = useCallback(() => {
    const h = cellHandlersRef.current;
    const isLocked = (e: Timesheet) => isEntryLocked(e, h.isDateLocked);
    // Per-CELL sweep: every clearable-empty draft entry goes, even from a row
    // that keeps real hours on other days. `releaseClaim` is set only for the
    // cells of a manual line that survives the sweep — a manual line whose every
    // cell is being cleared drops its whole scaffold instead (which releases all
    // its claims at once), mirroring the per-row trash button's `removesLine`.
    const deletions: Array<{ id: string; releaseClaim: boolean }> = [];
    const linesToRemove: RowKey[] = [];
    for (const row of rowsRef.current) {
      const clearable = rowEntriesOf(row).filter((e) => isClearableEmptyEntry(e, { isLocked }));
      if (clearable.length === 0) continue;
      const isManualLine = h.manualLineKeys.has(row.key);
      const removesLine = isManualLine && isEmptyClearableRow(row, { isLocked });
      if (removesLine) linesToRemove.push(row.key);
      for (const e of clearable) {
        deletions.push({ id: e.id, releaseClaim: isManualLine && !removesLine });
      }
    }
    if (deletions.length === 0) return;
    const count = deletions.length;
    let cleared = 0;
    let settled = 0;
    const onSettled = () => {
      settled += 1;
      if (settled < count) return;
      if (cleared === count) {
        toast.success(`Cleared ${count} empty ${count === 1 ? 'entry' : 'entries'}.`);
      } else if (cleared > 0) {
        toast.warning(`Cleared ${cleared} of ${count} empty entries; ${count - cleared} failed.`);
      }
      // cleared === 0 → every delete failed; the mutation's error toast (one
      // per distinct message) already told the story, so no summary is added.
    };
    for (const { id, releaseClaim } of deletions) {
      h.deleteEntry(id, {
        onSuccess: () => {
          if (releaseClaim) h.dropClaimedId(id);
          cleared += 1;
          onSettled();
        },
        onError: onSettled,
      });
    }
    // Drop wholly-emptied scaffolds after queuing the deletes; removeManualLine
    // releases their claims, so those entries carried `releaseClaim: false`.
    for (const key of linesToRemove) h.removeManualLine(key);
  }, []);

  // Expose addRow (for the page header's "+ Add row" button), the unsaved-
  // draft probe (for the page's leave-confirmation guard), the commit-focus
  // scroll (so a timer-produced row reveals itself), and the find navigation
  // (match count + open-by-index) to the route.
  useImperativeHandle(
    ref,
    () => ({
      addRow,
      hasUnsavedDraftRows,
      scrollEntryIntoView,
      getMatchCount,
      openMatchAt,
      clearEmptyEntries,
    }),
    [
      addRow,
      hasUnsavedDraftRows,
      scrollEntryIntoView,
      getMatchCount,
      openMatchAt,
      clearEmptyEntries,
    ],
  );

  // Apply a customer/product change to an existing row by patching every DRAFT
  // entry in parallel. Bulk row actions (rebind, trash) intentionally touch
  // only drafts — submitted/approved can't change, and rejected/failed need
  // individual attention (edit + resubmit), so they're handled one at a time
  // in the cell popover, not swept by a row-level control. `kept` is therefore
  // any non-draft (not just "submitted"), so the toast must not mislabel it.
  const rebindRow = (
    row: RowData,
    patch: {
      customerId?: string;
      productId?: string | null;
      engagementId?: string | null;
      hours?: number | null;
      description?: string | null;
    },
  ) => {
    const entries = rowEntriesOf(row);
    const drafts = entries.filter((e) => e.status === 'draft');
    const kept = entries.filter((e) => e.status !== 'draft');
    // The pickers are read-only unless the row has at least one draft and no
    // submitted/approved or period-locked entry (see `rowRebindable`), so by
    // the time we get here there is always a draft to move — this guard is a
    // defensive no-op, not a user-facing path. `kept` can only be
    // rejected/failed (still user-editable), which stay on the original row.
    if (drafts.length === 0) return;
    // An engagement's term can end inside the row's week, and the picker offers
    // one that covers ANY of the row's dates (`engagementCandidatesForRow`
    // unions across them) — so applying the pick to every draft would send the
    // days it does not cover to a guaranteed OUT_OF_RANGE rejection, rolling
    // each back with its own toast. Patch only the days the term actually
    // covers; the rest keep what they had, and the row splits, which is what
    // the data model says about a mid-week boundary.
    //
    // A pick that rides with an activity or customer MOVE is different: the
    // days outside the term still move (otherwise the row would split on the
    // activity), just without the pick — the server re-resolves their
    // engagement on their own date.
    const picked = patch.engagementId;
    const moves = patch.productId !== undefined || patch.customerId !== undefined;
    const covers = (e: Timesheet) =>
      engagementVersions.some((v) => v.engagementId === picked && coversDate(v, e.date));
    const applicable = picked == null ? drafts : drafts.filter(covers);
    for (const e of applicable) {
      patchEntry(e.id, patch);
    }
    if (moves && picked != null) {
      for (const e of drafts) {
        if (!covers(e)) patchEntry(e.id, { ...patch, engagementId: null });
      }
    } else if (applicable.length < drafts.length) {
      const skipped = drafts.length - applicable.length;
      toast.info(
        `Engagement applied to ${applicable.length} of ${drafts.length} days; ` +
          `${skipped} outside its term left unchanged.`,
      );
    }
    // With a move, every draft moves (those outside the term just without the
    // pick); without one, only the drafts the pick applied to.
    const moved = moves && picked != null ? drafts.length : applicable.length;
    if (kept.length > 0) {
      toast.info(
        `Moved ${moved} draft entr${moved === 1 ? 'y' : 'ies'}; ` +
          `${kept.length} non-draft entr${kept.length === 1 ? 'y' : 'ies'} kept on the original row.`,
      );
    }
  };

  // Update a manual line's picked customer/product/role (its metadata, not the
  // entries it owns). Used while filling in a freshly-added line and when
  // rebinding a populated one (the entry moves are handled separately).
  const updateManualLine = (
    key: RowKey,
    patch: Partial<Pick<ManualLineState, 'customerId' | 'productId' | 'engagementId' | 'roleId'>>,
  ) => {
    setManualLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  const removeManualLine = (key: RowKey) => {
    setManualLines((prev) => prev.filter((l) => l.key !== key));
  };

  // Shared updater for the claimed-id slots: re-maps every line's
  // `claimedIdsByDay` through `fn(currentId, dayIndex, line)`. The three claim
  // operations below differ only in that function.
  const mapManualClaims = (
    fn: (id: string | null, dayIndex: number, line: ManualLineState) => string | null,
  ) => {
    setManualLines((prev) =>
      prev.map((l) => ({ ...l, claimedIdsByDay: l.claimedIdsByDay.map((id, i) => fn(id, i, l)) })),
    );
  };

  // Claim a just-created entry's id onto a line's day column, so it renders on
  // the line and is excluded from auto-grouping. Replaces whatever id was in
  // that slot (e.g. a retry's new temp id supersedes the failed attempt's).
  const claimEntryAtDay = (key: RowKey, dayIndex: number, entryId: string) =>
    mapManualClaims((id, i, l) => (l.key === key && i === dayIndex ? entryId : id));

  // Re-point a claim from a temp id to its persisted real id once the POST
  // returns. Id-keyed (not slot-keyed) so it finds the cell regardless of which
  // day holds it; a no-op if the temp id is no longer claimed.
  const swapClaimedId = (fromId: string, toId: string) =>
    mapManualClaims((id) => (id === fromId ? toId : id));

  // Release a claim (failed create that rolled back, or a deleted entry). The
  // line stays; the cell just goes empty. A no-op if the id isn't claimed.
  const dropClaimedId = (entryId: string) => mapManualClaims((id) => (id === entryId ? null : id));

  // An UNATTRIBUTED row that exactly one engagement covers is bound WHOLE the
  // moment any of its cells is written. The server binds the written entry
  // itself, and the engagement is part of the row key — binding that entry
  // alone would split it off onto a row of its own, leaving its siblings
  // behind. Re-binds the row's other editable drafts and returns the
  // engagement the write should carry (null when the row isn't such a row).
  const bindUnattributedRow = (row: RowData | undefined, exceptId: string | null) => {
    if (!row || row.engagementId != null || !engagementsLoaded) return null;
    const inRow = rowEntriesOf(row);
    if (inRow.length === 0) return null;
    const resolution = resolveRowEngagement(
      engagementVersions,
      {
        customerId: row.customerId,
        productId: row.productId,
        engagementId: null,
        dates: inRow.map((e) => e.date),
      },
      weekStart,
    );
    if (resolution.kind !== 'bound') return null;
    const bound = resolution.engagement.engagementId;
    for (const e of inRow) {
      if (e.id === exceptId || e.engagementId != null || e.status !== 'draft') continue;
      if (isDateLocked(e.date, e.customerId)) continue;
      if (!engagementVersions.some((v) => v.engagementId === bound && coversDate(v, e.date))) {
        continue;
      }
      patchEntry(e.id, { engagementId: bound });
    }
    return bound;
  };

  // ── Stable per-cell save handlers ──
  // Each day cell is a `memo`'d CellSlot; if it received a freshly-built closure
  // every grid render, memo would never skip and one edit would re-render all
  // 7×rows cells (the "redraws on every change" slowness). The mutation helpers
  // (createEntry/patchEntry/…) are re-created each render, so we can't depend on
  // them directly. Instead keep the latest set in a ref (refreshed every render)
  // and expose three IDENTITY-STABLE handlers that read the freshest functions
  // at call time — no stale closures, no per-render churn. The cell passes its
  // row context in explicitly (it holds only primitive identity props), so these
  // handlers stay row-agnostic and shared across every cell.
  const cellHandlers = {
    createEntry,
    patchEntry,
    deleteEntry,
    recallEntry,
    claimEntryAtDay,
    swapClaimedId,
    dropClaimedId,
    // Also consumed by the stable `clearEmptyEntries` handle callback (defined
    // above, run only on user click) so it can drop manual-line scaffolds, tell
    // a manual line from a server row, and exclude locked cells — all with the
    // latest values.
    removeManualLine,
    // Lets a manual line's first create record the engagement it was bound to
    // on the line itself — see `onCellCreate`.
    updateManualLine,
    // Binds an unattributed row whole when one of its cells is written.
    bindUnattributedRow,
    rows,
    // The row's engagement is resolved for the ROW; the create is per CELL, so
    // `onCellCreate` re-checks the term against the cell's own date.
    engagementVersions,
    engagementsLoaded,
    manualLineKeys,
    isDateLocked,
  };
  const cellHandlersRef = useRef(cellHandlers);
  cellHandlersRef.current = cellHandlers;

  const onCellCreate = useCallback((ctx: CellCreateContext, value: CellEditorValue) => {
    if (!ctx.customerId) return;
    const h = cellHandlersRef.current;
    // The row binds; the CELL is what gets written. A term can end mid-week —
    // and a row that owns nothing resolves against the week's Monday, not this
    // cell's date — so the row's engagement is re-checked here against the day
    // actually being logged. Without it `create` throws OUT_OF_RANGE, the
    // optimistic row rolls back, and the hours the user just typed are gone,
    // with no way to clear the binding (the picker has no clear). Unattributed
    // is the right answer for a day the term doesn't reach; it is the same
    // rule `rebindRow` applies to a re-bind. Skipped until coverage has loaded
    // — an in-flight empty list would strip the engagement off every create.
    let engagementId = ctx.engagementId;
    if (engagementId != null && h.engagementsLoaded) {
      const bound = engagementId;
      const covered = h.engagementVersions.some(
        (v) => v.engagementId === bound && coversDate(v, ctx.date),
      );
      if (!covered) engagementId = null;
    }
    // A silently-bound engagement has to land on the LINE, not just on the
    // entry it was computed for. The line's own `engagementId` is what every
    // later cell is created with, and it only falls back to the resolution
    // while the line owns nothing — so without this the first cell of a manual
    // line is attributed and every one after it is not, splitting the line in
    // two on the next reload (the engagement is part of the row key).
    if (ctx.isManualLine && engagementId != null) {
      h.updateManualLine(ctx.lineKey, { engagementId });
    }
    // The new cell carries the row's resolved engagement; its unattributed
    // siblings move with it, so the row stays one row.
    if (engagementId != null) {
      h.bindUnattributedRow(
        h.rows.find((r) => r.key === ctx.lineKey),
        null,
      );
    }
    h.createEntry(
      {
        customerId: ctx.customerId,
        productId: ctx.productId,
        engagementId,
        date: ctx.date,
        hours: value.hours,
        description: value.description,
        billable: value.billable,
      },
      // Manual (kept-separate) lines CLAIM the new entry so it stays on this row
      // instead of auto-grouping; the claim rides the temp→real id swap and is
      // released on failure. Server rows pass nothing → unchanged auto-grouping.
      ctx.isManualLine
        ? {
            onOptimisticId: (tempId) => h.claimEntryAtDay(ctx.lineKey, ctx.dayIndex, tempId),
            onRealId: (tempId, realId) => h.swapClaimedId(tempId, realId),
            onError: (tempId) => h.dropClaimedId(tempId),
          }
        : undefined,
    );
  }, []);

  const onCellPatch = useCallback((id: string, value: CellEditorValue) => {
    const h = cellHandlersRef.current;
    // Editing a cell of an unattributed row the rule binds: the server binds
    // this entry, so bind the row with it (see `bindUnattributedRow`).
    const bound = h.bindUnattributedRow(
      h.rows.find((r) => rowEntriesOf(r).some((e) => e.id === id)),
      id,
    );
    h.patchEntry(id, {
      hours: value.hours,
      description: value.description,
      billable: value.billable,
      ...(bound != null ? { engagementId: bound } : {}),
    });
  }, []);

  const onCellDelete = useCallback((id: string) => {
    // Release the line's claim only once the DELETE succeeds (no-op for
    // non-manual rows) — dropping it eagerly would merge the entry back into the
    // auto-grouped row if the delete then failed and the row was restored.
    const h = cellHandlersRef.current;
    h.deleteEntry(id, { onSuccess: () => h.dropClaimedId(id) });
  }, []);

  const onCellRecall = useCallback((id: string) => {
    cellHandlersRef.current.recallEntry(id);
  }, []);

  // Daily totals + delta vs expected. Skip null hours (incomplete drafts
  // have no number to sum yet).
  const dailyTotals = useMemo(
    () =>
      dates.map((_, i) =>
        sumHours(
          rows.flatMap((r) => {
            const e = r.entriesByDay[i];
            return e?.hours != null ? [e.hours] : [];
          }),
        ),
      ),
    [rows, dates],
  );
  const weekTotal = sumHours(dailyTotals);
  // Over/under row measures against a flat 8h/day standard, independent of the
  // per-user hoursPerMonth capacity (which still drives the page header chip).
  const expectedPerWeek = STANDARD_HOURS_PER_WEEK;
  const expectedPerDay = STANDARD_HOURS_PER_DAY;

  // Keep this loading early return BELOW useImperativeHandle (line ~175) so
  // the page header's "+ Add row" button still works while the grid fetches.
  // If you move the return up, the handle won't register on the first paint
  // and `gridRef.current?.addRow()` becomes a silent no-op during loading.
  if (loading) {
    return (
      <div className="p-6">
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    // `flex-1 min-h-0` lets the grid claim the remaining viewport height so the
    // table can scroll internally with its header frozen. No own padding — the
    // enclosing PageShell supplies `p-6`, so the bordered table lines up edge-
    // to-edge with the page header and toolbar above it.
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {/* The bordered box fills the height and clips; the shadcn table-container
          inside it (a `data-slot=table-container` div we must not edit — G9) is
          turned into the scroll region from here, so the sticky header below
          pins to the top of *this* region instead of being trapped by the
          container's own `overflow-x-auto`. Scoping the scroll to the table —
          rather than the page — also stops rows peeking above the header
          through PageShell's top padding. */}
      <div
        ref={gridContainerRef}
        data-surface="table"
        className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border light:bg-card [&>[data-slot=table-container]]:min-h-0 [&>[data-slot=table-container]]:flex-1 [&>[data-slot=table-container]]:overflow-auto"
      >
        {/* Fixed layout so column widths come solely from the `w-X` header
            classes below and NEVER depend on cell content. Under the old
            auto layout the `w-X` was only a hint: the browser sized each
            column to its max-content, so picking a long customer/product
            name (the label span is `truncate` = nowrap, whose max-content
            is the full unwrapped string) or toggling between the static
            label and the autocomplete trigger re-measured the column and
            shoved every day column sideways — a visible "jump". With
            `table-fixed` the columns are pinned: `truncate` actually clips
            the Customer name and the "Out of scope" badge wraps within the
            Product column, with zero reflow on either change. `table-fixed`
            requires a definite table width, so `w-full` (shadcn's default)
            replaces the old `w-auto`.

            Column widths are sized so their natural sum (~1064px) fits a 13"
            laptop's content area — `w-full` then scales them up to fill wider
            screens, and the container only scrolls horizontally below that
            floor (a 7-day grid can't compress further without unusable day
            cells). Previously the columns summed to ~1272px and overflowed
            even a laptop, clipping Total + the last days off the right. */}
        <Table className="w-full table-fixed">
          {/* Sticky header: pinned to the top of the scroll region (the
              table-container, bounded above) as rows scroll under it. Needs an
              opaque background so scrolled rows don't bleed through —
              `light:bg-surface-subtle`, the dedicated "table headers, tab
              tracks" token (same as PillTabs and every other list table's
              header, see `sticky-table-scroll.tsx`), so the header reads as
              its own band above the white body rows. */}
          <TableHeader className="sticky top-0 z-20 light:bg-surface-subtle">
            <TableRow>
              <TableHead className="w-48">
                <SortHeader label="Customer" field="customer" sort={sort} onToggle={toggleSort} />
              </TableHead>
              <TableHead className="w-52">
                <SortHeader label="Activity" field="activity" sort={sort} onToggle={toggleSort} />
              </TableHead>
              {dates.map((d) => (
                <TableHead key={d} className="w-20 text-center">
                  <div className="text-[10px] uppercase tracking-wide">{shortDayName(d)}</div>
                  <div className="text-sm">{d.slice(8)}</div>
                </TableHead>
              ))}
              <TableHead className="w-16 text-right">Total</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={dates.length + 4} className="py-12 text-center">
                  <div className="text-sm font-medium">Nothing logged this week</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {weekFullyLocked
                      ? 'This week is locked and can no longer be edited.'
                      : 'Add a row to start entering hours.'}
                  </div>
                  {weekFullyLocked ? null : (
                    <Button
                      size="sm"
                      className="mt-4"
                      onClick={addRow}
                      aria-keyshortcuts={`${MOD_KEYSHORTCUT}+Enter`}
                    >
                      <Plus className="size-3.5" />
                      Add row
                      <ShortcutHint keys={MOD_ENTER_KEYS} className="ml-1 opacity-80" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              displayRows.map((row) => {
                const customer = row.customerId ? customerById.get(row.customerId) : undefined;
                // Read isInternal from `teamCustomers` (synchronously available)
                // rather than the lazily-fetched `customerById`, so a brand-new
                // draft pick is classified correctly right away. Internal
                // customers are never billable.
                const customerIsInternal = row.customerId
                  ? teamCustomers.find((c) => c.id === row.customerId)?.isInternal === true
                  : false;
                const product = row.productId
                  ? (productById.get(row.productId) ??
                    pickedProductsRef.current.get(row.productId) ??
                    null)
                  : null;
                // Short code of the role this row is LOGGED under (its pinned
                // `roleId`), shown as a chip before the activity name. This is
                // the role the entries actually bill at — which can differ from
                // the product's primary role (e.g. an activity logged under a
                // role listed in its "also visible to"). Undefined while the
                // role catalog loads or before a draft's role is resolved; the
                // chip is simply omitted then.
                const rowRoleCode = row.roleId ? serviceRoleCodeById.get(row.roleId) : undefined;
                // Each day column holds at most one entry; collect the row's
                // non-empty cells to compute row-level aggregates and signals.
                const rowEntries = rowEntriesOf(row);
                const rowTotal = sumHours(rowEntries.map((e) => e.hours ?? 0));
                const ready = row.customerId !== null;
                // Activity scope for this row, from the covering membership.
                // Empty = no membership covers the row's customer in this
                // week (e.g. an entry whose membership was ended/removed
                // after it was logged): the role is undeterminable and the
                // server would reject a product change, so the activity
                // picker is disabled rather than leaking the full catalog.
                const rowRoles = rolesForRow(row.customerId);
                const rowHasMembershipRole = rowRoles.length > 0;
                const editingCustomer = editing?.rowKey === row.key && editing.field === 'customer';
                const editingProduct = editing?.rowKey === row.key && editing.field === 'product';
                // The row's customer/activity (row-level attributes) and its
                // trash control act only on UNLOCKED draft entries: rebindRow
                // moves drafts; submitted/approved and period-locked entries
                // are immutable; rejected/failed are edited individually from
                // their cell popover. Keep the exact deletable set here so the
                // button's visibility and click handler cannot drift. Without
                // the period check, one click on a locked multi-day row sent a
                // DELETE per cell and produced one identical error toast each.
                const deletableDrafts = rowEntries.filter(
                  (e) => e.status === 'draft' && !isEntryLocked(e, isDateLocked),
                );
                const rowEditable = row.isDraft || deletableDrafts.length > 0;
                // Customer/activity, unlike the trash, are disabled entirely
                // when ANY entry in the row is submitted/approved (status-locked)
                // or sits on a period-locked date — the lock glyph the user sees
                // in those cells. Rather than open a picker and then refuse or
                // only partially apply the change, render the customer/activity
                // as static read-only labels. (rejected/failed entries are still
                // user-editable and carry no lock, so a row mixing drafts with
                // them stays rebindable; rebindRow moves the drafts and leaves
                // the rest on the original row.)
                const rowHasLockedEntry = rowEntries.some((e) => isEntryLocked(e, isDateLocked));
                const rowRebindable = rowEditable && !rowHasLockedEntry;
                // Row-level customer error: any entry on this row whose
                // submit-time validator flagged `customerId`. Surfaced on
                // the Customer cell so the user sees red where they need
                // to act. Picks the first message; the rest decay as the
                // user fixes them (page-level effect prunes the map).
                const rowCustomerError =
                  submitFieldErrors == null
                    ? undefined
                    : rowEntries
                        .map((e) => submitFieldErrors[e.id]?.customerId)
                        .find((m): m is string => m != null);
                // Row-level product error: mirrors `rowCustomerError`.
                // Product is a row attribute, so the submit validator
                // flags it per entry; surface the first message on the
                // Product cell so the user sees red where they must act.
                const rowProductError =
                  submitFieldErrors == null
                    ? undefined
                    : rowEntries
                        .map((e) => submitFieldErrors[e.id]?.productId)
                        .find((m): m is string => m != null);
                // Proactive pre-submit signal: the row carries hours but
                // no product, so submit will reject it. Mirrors the
                // missing-description red dot — surface it before the user
                // hits submit rather than only on failure. A row with no
                // hours yet has nothing to submit, so no nudge there.
                const rowNeedsProduct =
                  row.productId === null && rowEntries.some((e) => e.hours != null);
                const productMissing = rowProductError != null || rowNeedsProduct;
                // In-scope IDs for the row's current customer. `undefined`
                // (not an empty Set) when the data hasn't landed yet, so
                // the picker reads "no sectioning" until then instead of
                // mis-classifying every product as out-of-scope.
                const inScopeSet =
                  inScopeLoaded && row.customerId
                    ? inScopeByCustomer.get(row.customerId)
                    : undefined;
                const productOutOfScope =
                  row.productId !== null &&
                  inScopeSet !== undefined &&
                  !inScopeSet.has(row.productId);
                // Recently-used activities for the row's customer (ordered
                // most-recent-first) → the picker's Recent band.
                const recentSet = row.customerId ? recentByCustomer.get(row.customerId) : undefined;

                // Is this one of the user's manually-added (kept-separate) rows?
                // Its picker writes go to the line's own metadata, and — once it
                // owns entries — also move those drafts, so the line and its
                // entries stay in sync. A plain server row takes the rebind path.
                const isManualLine = manualLineKeys.has(row.key);
                const rowHasDraftEntry = rowEntries.some((e) => e.status === 'draft');
                // Fade rows with no find match while the find is actively
                // narrowing, so the matches stand out. Draft scaffolds the user
                // is still filling in never dim (they can't match anyway).
                const rowDimmed = findNarrowing && !row.isDraft && !matchingRowKeys.has(row.key);

                // Apply a picker change to a manual line: update its metadata,
                // and — if it already owns draft entries — move those too so the
                // line and its entries stay in sync (a fresh scaffold owns none).
                // Shared by both pickers, which differ only in the field written.
                const changeManualLine = (
                  metaPatch: Partial<
                    Pick<ManualLineState, 'customerId' | 'productId' | 'engagementId' | 'roleId'>
                  >,
                  rebindPatch: {
                    customerId?: string;
                    productId?: string | null;
                    engagementId?: string | null;
                    hours?: number | null;
                    description?: string | null;
                  },
                  shouldRebind: boolean,
                ) => {
                  updateManualLine(row.key, metaPatch);
                  if (shouldRebind && rowHasDraftEntry) rebindRow(row, rebindPatch);
                };

                // ── Engagement ──
                // Resolved from the row's OWN dates (see `resolveRowEngagement`),
                // and only once the coverage request has settled: an in-flight
                // empty list is indistinguishable from a genuine "nothing covers
                // this", and every row would flash "No engagement" first.
                const editingEngagement =
                  editing?.rowKey === row.key && editing.field === 'engagement';
                const engagementResolution: EngagementResolution = engagementsLoaded
                  ? resolveRowEngagement(
                      engagementVersions,
                      {
                        customerId: row.customerId,
                        productId: row.productId,
                        engagementId: row.engagementId,
                        dates: rowEntries.map((e) => e.date),
                      },
                      weekStart,
                    )
                  : { kind: 'pending' };
                // The dropdown's two sections. `candidates` are the engagements
                // the rule matched; `active` is everything live for the customer,
                // matching or not. Both are needed in EVERY state, not just the
                // ambiguous one: the field stays editable so a row nothing covers
                // can still be attributed deliberately.
                const rowEngagementCandidates = engagementsLoaded
                  ? engagementCandidatesForRow(
                      engagementVersions,
                      {
                        customerId: row.customerId,
                        productId: row.productId,
                        dates: rowEntries.map((e) => e.date),
                      },
                      weekStart,
                    )
                  : EMPTY_ENGAGEMENTS;
                const rowActiveEngagements = engagementsLoaded
                  ? engagementsActiveForRow(
                      engagementVersions,
                      {
                        customerId: row.customerId,
                        dates: rowEntries.map((e) => e.date),
                      },
                      weekStart,
                    )
                  : EMPTY_ENGAGEMENTS;
                // What a NEW cell in this row is created with: the row's stored
                // engagement, else the one the rule binds. The server binds an
                // unattributed entry the same way on every write, so sending the
                // resolution keeps the optimistic cell in the row the saved one
                // lands in — and `onCellCreate` binds the row's older
                // unattributed entries with it, so the row doesn't split.
                const rowEngagementId =
                  row.engagementId ??
                  (engagementResolution.kind === 'bound'
                    ? engagementResolution.engagement.engagementId
                    : null);
                // Row-level engagement error from the last submit — mirrors
                // `rowCustomerError` / `rowProductError`.
                const rowEngagementError =
                  submitFieldErrors == null
                    ? undefined
                    : rowEntries
                        .map((e) => submitFieldErrors[e.id]?.engagementId)
                        .find((m): m is string => m != null);
                // The prompt's question: about the activity PENDING a pick when an
                // activity change raised it, otherwise about the row as it is
                // (a submit that flagged it). `undefined` = no pending pick.
                const pendingRowActivity =
                  pendingActivity?.rowKey === row.key ? pendingActivity.product : undefined;
                const promptCandidates =
                  pendingRowActivity !== undefined
                    ? engagementCandidatesForRow(
                        engagementVersions,
                        {
                          customerId: row.customerId,
                          productId: pendingRowActivity?.id ?? null,
                          dates: rowEntries.map((e) => e.date),
                        },
                        weekStart,
                      )
                    : rowEngagementCandidates;
                const handleEngagementChange = (engagementId: string) => {
                  if (engagementId === row.engagementId) return;
                  if (isManualLine) {
                    changeManualLine({ engagementId }, { engagementId }, true);
                  } else {
                    rebindRow(row, { engagementId });
                  }
                };

                // When the row moves to `targetCustomerId`, can its current
                // activity still be logged there? Rows with no activity yet are
                // trivially fine. If the activity's detail hasn't loaded we err
                // on KEEP, so an in-flight catalog fetch can never silently
                // destroy the user's hours. See `isActivityVisibleForCustomer`.
                const activityStaysForCustomer = (targetCustomerId: string): boolean => {
                  if (!row.productId) return true;
                  const prod =
                    productById.get(row.productId) ??
                    pickedProductsRef.current.get(row.productId) ??
                    null;
                  if (!prod) return true;
                  const targetInternal =
                    teamCustomers.find((c) => c.id === targetCustomerId)?.isInternal === true;
                  return isActivityVisibleForCustomer(prod, {
                    customerId: targetCustomerId,
                    isInternal: targetInternal,
                    coveringRoleIds: rolesForRow(targetCustomerId),
                  });
                };

                // Clearing the activity on a customer change means the row's
                // draft hours no longer have a valid (customer, activity) pairing,
                // so DELETE those drafts rather than blank-moving them. A
                // blank-move PATCHes each entry to the new customer, which the
                // server rejects for any date outside that customer's team window
                // (NOT_ON_TEAM) — silently rolling the hours back and leaving the
                // row showing the new customer with its old hours. Delete never
                // touches the new customer/date, so the hours always clear.
                // Submitted/approved entries can't be deleted; rejected/failed are
                // cleared individually in the cell popover — only drafts go here.
                const deleteRowDrafts = () => {
                  for (const e of rowEntries.filter((e) => e.status === 'draft')) {
                    deleteEntry(
                      e.id,
                      isManualLine ? { onSuccess: () => dropClaimedId(e.id) } : undefined,
                    );
                  }
                };
                const performCustomerChange = (id: string | null) => {
                  const movingToNewCustomer = id != null && id !== row.customerId;
                  // The new customer can't perform the row's activity — drop the
                  // activity and delete the (draft) hours it carried for the OLD
                  // customer, leaving a blanked manual line on the new customer so
                  // the user can pick a valid activity.
                  const clearActivity = movingToNewCustomer && !activityStaysForCustomer(id);
                  if (isManualLine) {
                    if (clearActivity) {
                      // Switch the line to the new customer and blank its activity
                      // (local meta), then delete the draft hours it carried.
                      updateManualLine(row.key, {
                        customerId: id,
                        productId: null,
                        engagementId: null,
                        roleId: null,
                      });
                      deleteRowDrafts();
                      toast.info('Activity isn’t available for this customer — it was cleared.');
                    } else {
                      changeManualLine(
                        // An engagement belongs to ONE customer, so a move
                        // invalidates it. The server clears the stored column on
                        // the same patch; this keeps the line's own copy in step.
                        { customerId: id, engagementId: null },
                        { customerId: id ?? undefined },
                        movingToNewCustomer,
                      );
                    }
                  } else if (movingToNewCustomer) {
                    if (clearActivity) {
                      // A grouped server row is defined by its entries; deleting
                      // its drafts would remove the row entirely. Leave a blanked
                      // line on the new customer first so the row stays (blanked)
                      // for the user to re-pick a valid activity, then delete the
                      // drafts. Any kept non-draft entries stay on the original row.
                      addBlankLineForCustomer(id);
                      deleteRowDrafts();
                      toast.info('Activity isn’t available for this customer — it was cleared.');
                    } else {
                      rebindRow(row, { customerId: id });
                    }
                  } else if (id == null) {
                    // Clearing the customer on a saved row would orphan its
                    // draft entries (and be ambiguous in the week-grid where
                    // rows are grouped by customer×product). Surface the
                    // refusal so the user knows their click did something.
                    toast.info(
                      'Customer can’t be cleared on a saved row — delete the row to remove its entries.',
                    );
                  }
                  setEditing(null);
                };
                const handleCustomerChange = (id: string | null) => {
                  const movingToNewCustomer = id != null && id !== row.customerId;
                  const clearActivity = movingToNewCustomer && !activityStaysForCustomer(id);
                  // Only DRAFT hours get deleted (see `deleteRowDrafts`) — if
                  // there are none, nothing is actually lost and the swap can
                  // proceed straight away.
                  const willLoseDrafts =
                    clearActivity && rowEntries.some((e) => e.status === 'draft');
                  if (willLoseDrafts) {
                    setConfirmCustomerChange({
                      rowKey: row.key,
                      id,
                      newCustomerName:
                        teamCustomers.find((c) => c.id === id)?.name ?? 'this customer',
                    });
                    // Close the inline picker now — it's controlled by
                    // `row.customerId`, which hasn't changed, so it already
                    // renders back as the old customer underneath the dialog.
                    // A Cancel click needs no separate "revert" step.
                    setEditing(null);
                    return;
                  }
                  performCustomerChange(id);
                };
                // The question has to arrive IN the flow. A user who has just
                // chosen an activity is looking at the Activity column, not at a
                // line under Customer two columns to the left — leaving a red
                // hint there means "go back and find it".
                //
                // A DIALOG rather than the inline picker, because this fires
                // rarely (two engagements covering the same customer, date and
                // activity) and decides which contract the hours bill under. A
                // stop that seldom happens and lands on an invoice is worth
                // making deliberate; the same interruption on a common path
                // would be a tax on the fastest flow in the product.
                //
                // ONLY for `ask`. Zero matches is not a question (the rule never
                // asks there), so prompting would demand an answer the user does
                // not owe.
                const needsEngagementPick = (nextProductId: string | null): boolean => {
                  if (!engagementsLoaded || !rowRebindable) return false;
                  const next = resolveRowEngagement(
                    engagementVersions,
                    {
                      customerId: row.customerId,
                      productId: nextProductId,
                      // The stored value can't answer the NEW activity's
                      // question — resolve as if unanswered, which is what the
                      // row becomes once the activity moves under it.
                      engagementId: null,
                      dates: rowEntries.map((e) => e.date),
                    },
                    weekStart,
                  );
                  return next.kind === 'ask';
                };

                const handleProductChange = (picked: Product | null) => {
                  const id = picked?.id ?? null;
                  // Cache the full activity so the per-day cell gate can read its
                  // {primary} ∪ visibleTo role set this render, before the batched
                  // findByIds query catches up. See `pickedProductsRef`.
                  if (picked) pickedProductsRef.current.set(picked.id, picked);
                  // Re-picking the activity the row already has is not a change,
                  // and none of what follows may run for it: the engagement
                  // clear below would drop a manual line's stored value while
                  // leaving its entries' own value in place, and the prompt
                  // resolves as if unanswered, so it would re-ask a question the
                  // user has already answered.
                  if (id === row.productId) {
                    setEditing(null);
                    return;
                  }
                  // Several engagements cover the new activity: ask FIRST, and
                  // save the activity with the answer (see `pendingActivity`).
                  if (needsEngagementPick(id)) {
                    setPendingActivity({ rowKey: row.key, product: picked });
                    setEngagementPrompt(row.key);
                    setEditing(null);
                    return;
                  }
                  applyProductChange(picked, null);
                  setEditing(null);
                };

                // Move the row to `picked` with `engagementId` in the SAME patch.
                // Whatever engagement the row held was resolved against the OLD
                // activity — auto-bound to the one that covered it, or chosen
                // from the ones that did — and neither answer survives the
                // activity moving under it. Leaving it would silently keep
                // billing the new activity to the old activity's engagement,
                // which is precisely the misattribution this feature exists to
                // prevent. Null lets the server re-resolve from scratch: bind if
                // one covers, unattributed if none; the ambiguous case never
                // gets here without a pick (`needsEngagementPick`).
                //
                // (A customer change clears it too — server-side, since an
                // engagement belongs to one customer.)
                const applyProductChange = (
                  picked: Product | null,
                  engagementId: string | null,
                ) => {
                  const id = picked?.id ?? null;
                  if (isManualLine) {
                    // The recorded role IS the picked activity's own role — what
                    // the user chose in the picker. The server records the same
                    // (it derives the role from the activity, not the user's
                    // engagement role); pinning it here keeps the row's chip in
                    // sync immediately. Access is already enforced by the
                    // picker's role filter.
                    changeManualLine(
                      { productId: id, engagementId, roleId: picked?.serviceRoleId ?? null },
                      { productId: id, engagementId },
                      // Always a real change by here — the no-op returned above.
                      true,
                    );
                  } else {
                    rebindRow(row, { productId: id, engagementId });
                  }
                };

                return (
                  // hover:bg-transparent overrides the shadcn default
                  // hover:bg-muted/50 — cell-level focus already signals
                  // "this cell is active"; the row turning gray on top of
                  // that reads as a "row jump" when the popover opens.
                  <TableRow
                    key={row.key}
                    data-row-key={row.key}
                    data-find-dimmed={rowDimmed ? '' : undefined}
                    className={cn(
                      'group hover:bg-transparent [&>td]:align-middle',
                      // Fade non-matching rows during an active find. The cells
                      // stay interactive — dimming is a visual hint, not a lock.
                      rowDimmed && 'opacity-40 transition-opacity',
                    )}
                  >
                    <TableCell>
                      <div
                        // Per-row destructive treatment when the last
                        // submit flagged this row's customer. Wraps either
                        // the autocomplete or the label so the affordance
                        // is visible in both states.
                        className={cn(
                          rowCustomerError &&
                            'rounded-md ring-1 ring-destructive/60 bg-destructive/5',
                        )}
                        title={rowCustomerError ?? undefined}
                      >
                        {/* Holds the row's height while a wrapped (2+ line)
                            customer label swaps for the single-line editor —
                            without it the row collapses and every row below
                            jumps. See TimesheetWeekGridEditSlot. */}
                        <TimesheetWeekGridEditSlot editing={editingCustomer && rowRebindable}>
                          {editingCustomer && rowRebindable ? (
                            <CustomerAutocomplete
                              value={row.customerId}
                              defaultOpen
                              // Existing rows can swap customer but can't null it
                              // out — that would orphan the row's draft entries.
                              clearable={row.isDraft}
                              onChange={(c) => handleCustomerChange(c?.id ?? null)}
                              onOpenChange={(open) => {
                                if (!open) setEditing(null);
                              }}
                              placeholder="Pick customer…"
                              // Scope the picker to customers the target user
                              // is on the team of in this week. Server enforces
                              // the same rule on POST/PATCH (BusinessRuleError /
                              // NOT_ON_TEAM); the picker is just the UI
                              // affordance that prevents the invalid pick.
                              items={selectableCustomers}
                              selectedItem={
                                row.customerId
                                  ? (teamCustomers.find((c) => c.id === row.customerId) ?? null)
                                  : null
                              }
                            />
                          ) : (
                            <TimesheetLabelButton
                              onClick={() => setEditing({ rowKey: row.key, field: 'customer' })}
                              // Rows with a submitted/approved or period-locked
                              // entry can't be rebound — render the customer as a
                              // static label (no edit affordance) instead of a
                              // picker that would refuse the change.
                              readOnly={!rowRebindable}
                              ariaLabel="Edit customer"
                              // When a lock froze the row, explain why instead of
                              // just showing the customer name on hover.
                              title={
                                rowCustomerError ??
                                (rowHasLockedEntry ? LOCKED_ROW_REBIND_HINT : customer?.name)
                              }
                              // Always wrap so long customer names flow onto
                              // multiple lines within the fixed Customer column
                              // instead of being clipped with an ellipsis (e.g.
                              // "Vericred, Inc. DBA Ideon -R- M…").
                              wrap
                            >
                              {row.customerId ? (
                                <span className="text-sm font-medium">
                                  {customer?.name ? (
                                    <HighlightedText text={customer.name} query={findHighlight} />
                                  ) : (
                                    '…'
                                  )}
                                </span>
                              ) : (
                                <span
                                  className={cn(
                                    'text-sm italic',
                                    rowCustomerError ? 'text-destructive' : 'text-muted-foreground',
                                  )}
                                >
                                  {rowCustomerError ? 'Pick a customer' : 'Pick customer…'}
                                </span>
                              )}
                            </TimesheetLabelButton>
                          )}
                        </TimesheetWeekGridEditSlot>
                        {/* Engagement rides under the customer rather than in
                            its own column: the grid is already seven day
                            columns wide, and the engagement is an attribute of
                            the customer binding, not a third axis. Renders
                            nothing until the row has both a customer and an
                            activity. */}
                        <TimesheetEngagementCell
                          resolution={engagementResolution}
                          candidates={rowEngagementCandidates}
                          activeEngagements={rowActiveEngagements}
                          readOnly={!rowRebindable}
                          error={rowEngagementError}
                          open={editingEngagement}
                          onOpenChange={(next) =>
                            setEditing(next ? { rowKey: row.key, field: 'engagement' } : null)
                          }
                          onChange={handleEngagementChange}
                        />
                        {/* The forced ask. Radix renders nothing while closed,
                            so this costs a row nothing until it fires. */}
                        <TimesheetEngagementDialog
                          open={engagementPrompt === row.key}
                          customerName={customer?.name}
                          activityName={
                            pendingRowActivity !== undefined
                              ? pendingRowActivity?.name
                              : product?.name
                          }
                          candidates={promptCandidates}
                          others={rowActiveEngagements.filter(
                            (e) => !promptCandidates.some((c) => c.engagementId === e.engagementId),
                          )}
                          currentEngagementId={
                            pendingRowActivity !== undefined ? null : row.engagementId
                          }
                          onSelect={(id) => {
                            if (pendingRowActivity !== undefined) {
                              applyProductChange(pendingRowActivity, id);
                            } else {
                              handleEngagementChange(id);
                            }
                            setPendingActivity(null);
                            setEngagementPrompt(null);
                          }}
                          // Cancelling an activity pick leaves the row on its
                          // old activity — the pick was never saved.
                          onDismiss={() => {
                            setPendingActivity(null);
                            setEngagementPrompt(null);
                          }}
                        />
                        <ConfirmDialog
                          open={confirmCustomerChange?.rowKey === row.key}
                          title="Change customer?"
                          description={
                            <>
                              {product?.name ? `“${product.name}” isn’t` : 'This activity isn’t'}{' '}
                              available for{' '}
                              {confirmCustomerChange?.rowKey === row.key
                                ? confirmCustomerChange.newCustomerName
                                : ''}
                              . Continuing will clear the activity and delete this row’s unsaved
                              hours for {customer?.name ?? 'the current customer'}. This can’t be
                              undone.
                            </>
                          }
                          confirmLabel="Change customer"
                          cancelLabel="Keep current customer"
                          destructive
                          onConfirm={() => {
                            if (confirmCustomerChange?.rowKey === row.key) {
                              performCustomerChange(confirmCustomerChange.id);
                            }
                            setConfirmCustomerChange(null);
                          }}
                          onCancel={() => setConfirmCustomerChange(null)}
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div
                        // Per-row destructive treatment when the last
                        // submit flagged this row's product — mirrors the
                        // customer cell so the affordance is visible in
                        // both the editing and label states.
                        className={cn(
                          rowProductError &&
                            'rounded-md ring-1 ring-destructive/60 bg-destructive/5',
                        )}
                        title={rowProductError ?? undefined}
                      >
                        {/* Same height hold as the customer cell: a wrapped
                            activity label (long name, role chip, badge) must
                            not collapse the row when the editor opens. */}
                        <TimesheetWeekGridEditSlot
                          editing={editingProduct && rowRebindable && rowHasMembershipRole}
                        >
                          {editingProduct && rowRebindable && rowHasMembershipRole ? (
                            <ProductAutocomplete
                              value={row.productId}
                              defaultOpen
                              // Read isInternal from `teamCustomers` (the prop,
                              // synchronously available) not the lazily-fetched
                              // `customerById` — a brand-new draft pick wouldn't
                              // be in customerById yet, flickering the catalog.
                              // Role scoping applies to the internal customer too;
                              // ProductAutocomplete only drops SOW sectioning for
                              // it. Pass normal props + flag.
                              filterByInternal={
                                teamCustomers.find((c) => c.id === row.customerId)?.isInternal
                              }
                              onChange={(p) => handleProductChange(p)}
                              onOpenChange={(open) => {
                                if (!open) setEditing(null);
                              }}
                              placeholder="Pick activity…"
                              // Surface this row's customer's custom activities
                              // alongside the global catalog (and exclude other
                              // customers'). Server matches on write.
                              customerId={row.customerId}
                              // Scope data is withheld while the out-of-scope UI
                              // is hidden, so the dropdown drops its
                              // "In/Out of scope" sections, amber dots, and
                              // trigger badge. Same flag as the standalone badge
                              // so both restore together.
                              inScopeProductIds={SHOW_OUT_OF_SCOPE_BADGE ? inScopeSet : undefined}
                              // Scope the catalog to the assignee's
                              // PER-CUSTOMER role(s) (from
                              // customer_team_members) for this row's customer
                              // across the visible week — NOT the global
                              // user.serviceRoleId. A user can serve different
                              // roles on different customers; the picker must
                              // reflect what they can actually log against on
                              // THIS customer. Server enforces per-day on
                              // write. See `rolesForRow` for the union logic
                              // (mid-week role change → both roles included).
                              filterServiceRoleIds={rowRoles}
                              recentProductIds={recentSet}
                            />
                          ) : ready && !rowHasMembershipRole ? (
                            // No covering membership for this row's customer in
                            // the visible week — the role is undeterminable and
                            // the server would reject a product change, so the
                            // activity is read-only. Show the existing product
                            // name if any, else an explanatory placeholder.
                            <span
                              className="text-sm italic text-muted-foreground"
                              title="No active team membership for these dates — the activity can't be changed."
                            >
                              {product?.name ?? 'No team membership'}
                            </span>
                          ) : ready ? (
                            <TimesheetLabelButton
                              onClick={() => setEditing({ rowKey: row.key, field: 'product' })}
                              // Rows with a submitted/approved or period-locked
                              // entry can't be rebound — render the activity as a
                              // static label (no edit affordance) instead of a
                              // picker that would refuse the change.
                              readOnly={!rowRebindable}
                              ariaLabel="Edit activity"
                              // When a lock froze the row, explain why instead of
                              // just showing the activity name on hover.
                              title={
                                rowHasLockedEntry
                                  ? LOCKED_ROW_REBIND_HINT
                                  : product
                                    ? product.name
                                    : undefined
                              }
                              // Always wrap so long activity names flow onto
                              // multiple lines within the fixed Activity column
                              // instead of being clipped with an ellipsis. (The
                              // trailing "Out of scope" badge already relied on
                              // this; now it applies to plain long names too.)
                              wrap
                            >
                              {row.productId ? (
                                product ? (
                                  // Inline flow (not flex) so the badge sits next to
                                  // the name when there's room and wraps below it when
                                  // there isn't — without truncating the product name.
                                  // The leading role chip disambiguates products that
                                  // share a name across service roles, matching the
                                  // picker's closed-trigger chip.
                                  <span className="text-sm">
                                    {rowRoleCode ? (
                                      <>
                                        <ServiceRoleChip code={rowRoleCode} />{' '}
                                      </>
                                    ) : null}
                                    <HighlightedText text={product.name} query={findHighlight} />
                                    {SHOW_OUT_OF_SCOPE_BADGE && productOutOfScope ? (
                                      <>
                                        {' '}
                                        <OutOfScopeBadge className="align-middle" />
                                      </>
                                    ) : null}
                                  </span>
                                ) : (
                                  <span className="text-sm text-muted-foreground">…</span>
                                )
                              ) : (
                                <span className="inline-flex items-center gap-1">
                                  {productMissing ? (
                                    <span
                                      aria-hidden
                                      title="Pick an activity before submitting"
                                      className="inline-block size-1.5 shrink-0 rounded-full bg-red-500"
                                    />
                                  ) : null}
                                  <span
                                    className={cn(
                                      'text-sm italic',
                                      productMissing ? 'text-destructive' : 'text-muted-foreground',
                                    )}
                                  >
                                    {productMissing ? 'Pick an activity' : 'Pick activity…'}
                                  </span>
                                </span>
                              )}
                            </TimesheetLabelButton>
                          ) : (
                            // Disabled-until-customer: render a static, non-
                            // interactive placeholder. The customer cell sits
                            // to the left with the obvious next action — no
                            // toast needed, the placeholder text alone tells
                            // the story. No `aria-disabled` here: it's a
                            // non-interactive element, there's no implicit
                            // interactability to disable.
                            <div className="inline-flex min-h-9 w-full items-center px-2 py-1.5 text-sm italic text-muted-foreground/60">
                              Pick customer first
                            </div>
                          )}
                        </TimesheetWeekGridEditSlot>
                      </div>
                    </TableCell>
                    {row.entriesByDay.map((dayEntry, i) => {
                      const date = dates[i];
                      if (!date) return null;
                      // Per-day team-membership gate. Even if the row's
                      // customer is on the target's team somewhere in the
                      // week, only days inside the membership window are
                      // editable. Backend enforces the same rule on POST/
                      // PATCH (NOT_ON_TEAM 422); this is the UI affordance.
                      const offTeamForDay =
                        ready && row.customerId ? !isOnTeamForDay(row.customerId, date) : false;
                      // The CELL's own stage, matching the row-level
                      // `isEntryLocked` above: an existing `rejected` /
                      // `failed` entry is judged on the settlement boundary,
                      // so it stays editable through the settlement window.
                      // An empty cell has no row yet, so it is new work and
                      // takes the submission boundary (the `'draft'` default).
                      // Asking the bare submission boundary here would gate
                      // the cell while the row said it was editable — one
                      // component, two answers.
                      const periodLocked = isDateLocked(
                        date,
                        row.customerId,
                        timesheetLockStageFor('edited', dayEntry?.status ?? 'draft'),
                      );
                      // The row's activity can only log on the days a covering
                      // membership makes it loggable (its {primary} ∪ visibleTo
                      // role set — a role added mid-week doesn't cover the earlier
                      // days). Disable those cells; the same activity under a role
                      // that covers them is a separate row. Only gates once an
                      // activity (or at least a role) is pinned.
                      const roleNotCoveringDay =
                        ready && row.customerId
                          ? !activityCoversDate(row.customerId, product, row.roleId, date)
                          : false;
                      // Period lock no longer HARD-disables the cell — a locked
                      // cell with entries opens read-only so the rejection
                      // reason / details stay inspectable (CellSlot gates the
                      // editing). Off-team / no-customer / role-not-active are
                      // the hard blocks.
                      const cellDisabled = !ready || offTeamForDay || roleNotCoveringDay;
                      const cellDisabledReason = !ready
                        ? 'Pick a customer first'
                        : offTeamForDay
                          ? 'Not on this customer’s team on this day'
                          : roleNotCoveringDay
                            ? 'This activity’s service role isn’t active on this day'
                            : periodLocked
                              ? 'This period is locked.'
                              : undefined;
                      const isActiveTimer =
                        runningTimer != null &&
                        runningTimer.timer.customerId === row.customerId &&
                        runningTimer.timer.productId === row.productId &&
                        runningTimer.timer.workDate === date;
                      // Submit-time field error for this cell's entry (missing
                      // hours OR description — mutually exclusive on one entry,
                      // since the backend only emits the description error when
                      // hours is present). Drives the destructive cell face.
                      const submitError =
                        dayEntry && submitFieldErrors
                          ? (submitFieldErrors[dayEntry.id]?.hours ??
                            submitFieldErrors[dayEntry.id]?.description)
                          : undefined;
                      // Default billable for a NEW entry here: inherit from the
                      // day's most-recent same-pair entry (sibling cell on
                      // another row); with no prior entry, follow the row's
                      // customer — external defaults billable, internal defaults
                      // off (and the editor forces internal off regardless).
                      const cellDefaultBillable = stickyBillable(
                        siblingsByCell.get(
                          `${row.customerId ?? ''}|${row.productId ?? ''}|${date}`,
                        ) ?? [],
                        !customerIsInternal,
                      );
                      return (
                        <TableCell key={`${row.key}-${i}`} className="p-1 text-center">
                          <CellSlot
                            date={date}
                            dayIndex={i}
                            entry={dayEntry}
                            disabled={cellDisabled}
                            disabledReason={cellDisabledReason}
                            periodLocked={periodLocked}
                            productDescription={product?.description}
                            customerIsInternal={customerIsInternal}
                            defaultBillable={cellDefaultBillable}
                            isActiveTimer={isActiveTimer}
                            submitError={submitError}
                            // Day-column total minus this cell's own hours →
                            // the "other entries" the 24h/day cap measures
                            // against. dailyTotals already skips null hours.
                            otherHoursOnDay={(dailyTotals[i] ?? 0) - Number(dayEntry?.hours ?? 0)}
                            // Row identity travels as primitive props so the
                            // shared, identity-stable save handlers below can act
                            // without a per-cell closure — that's what lets the
                            // memo'd cell skip re-rendering when a sibling changes.
                            rowCustomerId={row.customerId}
                            rowProductId={row.productId}
                            // The row's resolved engagement, so a cell created
                            // here is attributed at birth. `bound` is the only
                            // state that yields one — an unanswered `ask` writes
                            // null and submit is what refuses it.
                            rowEngagementId={rowEngagementId}
                            lineKey={row.key}
                            isManualLine={isManualLine}
                            open={openCell?.rowKey === row.key && openCell.dayIndex === i}
                            onOpenChange={onCellOpenChange}
                            onCreate={onCellCreate}
                            onPatch={onCellPatch}
                            onDelete={onCellDelete}
                            onRecall={onCellRecall}
                          />
                        </TableCell>
                      );
                    })}
                    <TableCell className="text-right text-sm font-semibold tabular-nums">
                      {rowTotal > 0 ? formatHours(rowTotal) : '—'}
                    </TableCell>
                    <TableCell className="p-1 text-right">
                      {rowEditable ? (
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          aria-label={
                            row.isDraft
                              ? 'Remove this empty row'
                              : "Delete this row's draft hours for the week"
                          }
                          title={
                            row.isDraft
                              ? 'Remove this empty row'
                              : "Delete this row's draft hours for the week"
                          }
                          className="opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                          disabled={deleting}
                          onClick={() => {
                            if (row.isDraft) {
                              // Empty scaffold — just drop the manual line.
                              if (isManualLine) removeManualLine(row.key);
                              return;
                            }
                            // Delete every unlocked DRAFT entry in this row.
                            // Locked drafts and non-draft entries stay;
                            // rejected/failed are handled individually in the
                            // cell popover.
                            const drafts = deletableDrafts;
                            const kept = rowEntries.filter((e) => !drafts.includes(e));
                            // A manual line only exists to keep a duplicate
                            // separate; once its drafts are gone and nothing is
                            // kept, drop the now-empty scaffold too (otherwise it
                            // would linger as a blank row). If non-draft entries
                            // remain, the line stays to hold them.
                            const removesLine = isManualLine && kept.length === 0;
                            for (const e of drafts) {
                              // When the whole line is going away, removeManualLine
                              // below releases its claims; otherwise release each
                              // claim only once its DELETE lands (see onDelete).
                              deleteEntry(
                                e.id,
                                removesLine ? undefined : { onSuccess: () => dropClaimedId(e.id) },
                              );
                            }
                            if (removesLine) removeManualLine(row.key);
                            if (kept.length > 0) {
                              toast.info(
                                `Removed ${drafts.length} draft entr${drafts.length === 1 ? 'y' : 'ies'}; ${kept.length} locked or non-draft entr${kept.length === 1 ? 'y' : 'ies'} kept.`,
                              );
                            }
                          }}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
            {/* Ghost row: a permanent "+ Add row" line rendered AFTER the mapped
                rows, so the affordance always sits directly under the row the
                user just finished — no scrolling back to the page header. It is
                deliberately outside `displayRows` (and therefore outside the
                sort, the totals, and the find/submit counts): it is chrome, not
                a row. Suppressed on the empty state, which already offers its
                own centred Add row button, and when every day is locked, where
                a new row could only yield read-only cells. */}
            {rows.length === 0 || weekFullyLocked ? null : (
              <TableRow className="group/add hover:bg-transparent">
                <TableCell colSpan={dates.length + 4} className="p-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={addRow}
                    aria-keyshortcuts={`${MOD_KEYSHORTCUT}+Enter`}
                    className="h-9 w-full justify-start rounded-none border-t border-dashed px-4 font-normal text-muted-foreground hover:text-foreground"
                  >
                    <Plus className="size-3.5" />
                    Add row
                    {/* Sits immediately after the label, NOT flushed to the far
                        end of the row: across a full-width week grid that is a
                        thousand pixels of gap, and the hint stops reading as
                        part of the same control. Muted until hover/focus so a
                        permanently-visible row of chrome doesn't compete with
                        the data above it. */}
                    <ShortcutHint
                      keys={MOD_ENTER_KEYS}
                      className="ml-1 opacity-70 transition-opacity group-hover/add:opacity-100 group-focus-within/add:opacity-100"
                    />
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {/* Pinned to the bottom of the scroll region (mirror of the sticky
              header above) so the day/week totals stay readable without
              scrolling to the end of a long week. Needs an opaque background —
              `light:bg-card` (white) overrides shadcn's translucent
              `bg-muted/50` — so scrolled rows don't bleed through, and matches
              the white table card rather than the cream page. */}
          {rows.length > 0 ? (
            <TableFooter className="sticky bottom-0 z-20 light:bg-card font-normal">
              <TotalsFooter
                dailyTotals={dailyTotals}
                weekTotal={weekTotal}
                expectedPerDay={expectedPerDay}
                expectedPerWeek={expectedPerWeek}
                dates={dates}
              />
            </TableFooter>
          ) : null}
        </Table>
      </div>

      {promptHost}
    </div>
  );
}

const CellSlot = memo(function CellSlot({
  date,
  dayIndex,
  entry,
  disabled,
  disabledReason,
  periodLocked,
  productDescription,
  customerIsInternal,
  defaultBillable,
  isActiveTimer,
  submitError,
  otherHoursOnDay,
  rowCustomerId,
  rowProductId,
  rowEngagementId,
  lineKey,
  isManualLine,
  open,
  onOpenChange,
  onCreate,
  onPatch,
  onDelete,
  onRecall,
}: {
  date: string;
  /** 0..6, Mon..Sun. Emitted as the trigger's `data-ts-cell` so tests
   *  (and any future per-column navigation) can target a specific day. */
  dayIndex: number;
  /** The single entry for this (customer, product, date), or `null` for an
   *  empty cell. Extra entries for the same day live on their own duplicate
   *  rows, so a cell never holds more than one. */
  entry: Timesheet | null;
  disabled: boolean;
  disabledReason?: string;
  /** The date's period has locked — render the entry read-only with a
   *  period-lock label (distinct from status-driven locks). */
  periodLocked: boolean;
  /** Pre-fills the cell editor's description on new entries. Pulled from
   *  the row's product (already loaded once by the parent's batched
   *  `productsApi.findByIds` query — no per-cell fetch). */
  productDescription?: string | null;
  /** The row's customer is internal — its time is never billable. Forces
   *  the cell editor's billable toggle off + disabled. */
  customerIsInternal?: boolean;
  /** Default billable for a NEW entry created in this empty cell — the sticky
   *  value derived from the day's other same-pair entries. Ignored when editing
   *  an existing entry (its own billable wins) or for internal customers. */
  defaultBillable?: boolean;
  /** True when an active timer matches this cell's (customer, product, date).
   *  Renders a small pulsing dot in the bottom-left corner (its own channel,
   *  so the status dot can't hide it). pointer-events-none. */
  isActiveTimer?: boolean;
  /** Submit-time field error for this cell's entry (missing hours OR
   *  description). Drives the destructive border + tooltip on the cell. */
  submitError?: string;
  /** Sum of the day column's OTHER entries (excluding this cell), for the
   *  inline 24h/day cap in the editor. */
  otherHoursOnDay?: number;
  /** Row identity for `onCreate` — primitives so the cell stays `memo`-stable
   *  and the shared create handler can act without a per-cell closure. */
  rowCustomerId: string | null;
  rowProductId: string | null;
  rowEngagementId: string | null;
  lineKey: RowKey;
  isManualLine: boolean;
  /** Whether THIS cell's editor popover is open. Lifted to the grid (a single
   *  `openCell`) so find navigation can open a cell programmatically; a
   *  primitive boolean here keeps the memo skipping every non-transitioning
   *  cell. */
  open: boolean;
  /** Identity-stable open toggler shared across every cell — the cell hands
   *  back its own `(lineKey, dayIndex)` so no per-cell closure is minted. */
  onOpenChange: (rowKey: RowKey, dayIndex: number, next: boolean) => void;
  /** Identity-stable save handlers shared across every cell (see the grid's
   *  `onCellCreate`/`onCellPatch`/`onCellDelete`). `onCreate` takes the cell's
   *  row context explicitly since the handler itself is row-agnostic. */
  onCreate: (ctx: CellCreateContext, value: CellEditorValue) => void;
  onPatch: (id: string, value: CellEditorValue) => void;
  onDelete: (id: string) => void;
  /** Recall a submitted entry back to draft (identity-stable; see the
   *  grid's `onCellRecall`). */
  onRecall: (id: string) => void;
}) {
  // `open` is controlled by the grid; this delegates every local open/close to
  // the shared toggler, keeping all the focus-dance call sites below unchanged.
  const setOpen = (next: boolean) => onOpenChange(lineKey, dayIndex, next);
  // The cell shows non-editable hours; all data entry happens in the
  // popover. Focusing the cell (via Tab) auto-opens it so keyboard users
  // can type straight away, then close (Enter / Save / Esc) and Tab to the
  // next day. Two refs keep that from fighting Radix's own focus handling:
  //   - skipReopenRef: on close, the dismissable layer returns focus to
  //     this trigger. Without suppression that returning focus would
  //     instantly re-open the popover, so Esc / Save could never settle it
  //     closed. We raise it on every close and consume it on the returning
  //     focus — the cell ends up focused-but-closed, and the next Tab moves
  //     on to the next day.
  //   - pointerDownRef: a mouse press also focuses the trigger, but there
  //     the trigger's own click should toggle. Opening on that focus would
  //     open-then-close on the same press, so we skip the focus-open when a
  //     pointer initiated it and let the click do the work.
  const skipReopenRef = useRef(false);
  const pointerDownRef = useRef(false);
  // The editor reports whether it holds unsaved edits. Kept in a ref (read at
  // dismiss time, never rendered) so the outside-click guard can decide without
  // re-rendering the cell on every keystroke.
  const dirtyRef = useRef(false);
  const handleDirtyChange = useCallback((next: boolean) => {
    dirtyRef.current = next;
  }, []);
  // Shows the "discard unsaved changes?" prompt when an outside-click would
  // otherwise drop a dirty draft.
  const [showDiscard, setShowDiscard] = useState(false);

  // ── Single-entry cell signals ──
  const value = Number(entry?.hours ?? 0);
  const filled = value > 0;
  // A rejected/failed entry drives the tint + status dot.
  const attention = entry && isAttentionStatus(entry.status) ? entry : null;
  // submitted/approved → read-only look + lock glyph. Tracked apart from
  // `periodLocked` (this cell needs each half on its own — see `readOnly` /
  // `lockNote` below); the combined row-level view lives in `isEntryLocked`,
  // which shares this same `isTimesheetEditable` status rule.
  const locked = entry != null && !isTimesheetEditable(entry.status);
  const lockedLook = periodLocked || locked;

  // A period-locked cell WITH an entry stays openable but READ-ONLY, so the
  // rejection reason / details remain inspectable; an empty locked cell (and
  // off-team / no-customer cells) can't open. A submitted/approved entry opens
  // read-only too (handled inside the popover via `isTimesheetEditable`).
  const readOnly = periodLocked;
  const interactive = !disabled && (entry != null || !periodLocked);
  // Accessible name carries the lock state so screen-reader users get it
  // without relying on the decorative (aria-hidden) Lock glyph.
  const lockNote = periodLocked
    ? 'period locked'
    : entry?.status === 'approved'
      ? 'approved'
      : locked
        ? 'contains locked entries'
        : null;

  // Close the popover and suppress the auto-reopen for the focus that the
  // dismissable layer returns to the trigger. Used by every close path
  // (Esc / outside-click via onOpenChange, and Save / Clear / Cancel from
  // the editor) so the cell reliably settles closed-but-focused.
  const closeCell = () => {
    skipReopenRef.current = true;
    dirtyRef.current = false;
    setShowDiscard(false);
    setOpen(false);
  };

  // Tint the whole cell by approval outcome so a rejected / failed entry is
  // identifiable at a glance. Layered AFTER the filled/locked base so its
  // colours win (twMerge), but BEFORE the open-ring / submitError treatments.
  const statusTint =
    attention?.status === 'rejected'
      ? 'border-red-300 border-l-2 border-l-red-500 bg-red-50 dark:border-red-800/60 dark:bg-red-900/20'
      : attention?.status === 'failed'
        ? 'border-amber-300 border-l-2 border-l-amber-500 bg-amber-50 dark:border-amber-800/60 dark:bg-amber-900/20'
        : null;

  // Independent signals live in separate corners so none shadows another:
  //   top-left  = status dot (attention / missing-description)
  //   top-right = lock glyph when the entry is submitted/approved (or period-locked)
  //   bottom-left = running-timer pulsing dot (own channel, never hidden by status)
  // Billable is intentionally NOT shown — it can't be carried legibly on a
  // 32px cell; the popover editor surfaces it.
  const markers = (
    <>
      {entry ? (
        <EntryStatusDot entry={entry} className="pointer-events-auto absolute left-1 top-1" />
      ) : null}
      {locked || periodLocked ? (
        <Lock
          aria-hidden
          className={cn(
            'pointer-events-none absolute right-1 top-1 size-2.5',
            // Colour carries the lock's meaning, not just "read-only":
            //   period locked → red    (the whole period is closed)
            //   approved      → green  (signed off)
            //   submitted / other locked → neutral grey (awaiting approval)
            // Period lock wins, matching `lockNote`'s precedence below.
            periodLocked
              ? 'text-red-600 dark:text-red-500'
              : entry?.status === 'approved'
                ? 'text-green-600 dark:text-green-500'
                : 'text-muted-foreground/70',
          )}
        />
      ) : null}
      {isActiveTimer ? (
        <PulsingDot size="sm" className="pointer-events-none absolute bottom-1 left-1" />
      ) : null}
    </>
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!interactive) return;
        // Radix-driven closes (Esc, outside-click, clicking an open
        // trigger): route through closeCell so the returning focus doesn't
        // auto-reopen the cell.
        if (!next) {
          closeCell();
          return;
        }
        setOpen(true);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={!interactive}
          data-ts-cell={dayIndex}
          // Per-day a11y label + lock state so screen-reader users get column
          // context and know the cell holds locked work.
          aria-label={`Hours, ${longDayName(date)}${lockNote ? ` — ${lockNote}` : ''}`}
          title={submitError ?? disabledReason}
          onPointerDown={() => {
            pointerDownRef.current = true;
          }}
          onClick={() => {
            // The trigger's own click handled this activation — clear the
            // pointer flag for the next interaction.
            pointerDownRef.current = false;
          }}
          onFocus={() => {
            // Focus returning after a close: consume the suppression flag
            // and stay closed so the next Tab advances to the next day.
            if (skipReopenRef.current) {
              skipReopenRef.current = false;
              return;
            }
            // Mouse activation: let the trigger's click toggle it instead,
            // so we don't open-then-close on the same press.
            if (pointerDownRef.current) return;
            // Keyboard focus (Tab / arrow): auto-open for immediate entry.
            setOpen(true);
          }}
          className={cn(
            'group/cell relative mx-auto flex h-8 w-full max-w-[80px] items-center justify-center rounded-full border px-1 text-center text-sm num outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40',
            filled || lockedLook
              ? 'border-border bg-muted/40 font-medium'
              : 'border-dashed border-border/70 text-muted-foreground',
            lockedLook && 'text-muted-foreground',
            interactive && 'hover:border-primary/40',
            statusTint,
            // Off-team / no-customer cells render faint + not-allowed; locked
            // cells stay readable (muted, not faded) with a default cursor.
            !interactive && !lockedLook && 'cursor-not-allowed opacity-40 hover:border-border',
            !interactive && lockedLook && 'cursor-default',
            open && 'border-primary ring-2 ring-inset ring-primary/30',
            // Destructive treatment for cells the submit flagged. Wins over the
            // base border; the open-popover ring still overrides.
            submitError && !open && 'border-destructive bg-destructive/5',
          )}
        >
          {/* An entry that exists but has no hours yet (e.g. a row copied
              from last week with hours blanked) shows "0h" so it reads as a
              real, present cell awaiting input; "—" is reserved for cells with
              no entry at all, keeping the two visually distinct. */}
          {filled ? formatHours(value) : entry ? '0h' : '—'}
          {markers}
        </button>
      </PopoverTrigger>
      {/* Capped at the viewport space Radix measures, and scrolls inside: a
          rejected entry adds its reason banner, and at 100% zoom the editor
          could run off-screen with Save unreachable. The editor pins its
          Save row to this box's bottom edge (it assumes this `p-3`). */}
      <PopoverContent
        className="max-h-(--radix-popover-content-available-height) w-80 overflow-y-auto p-3"
        collisionPadding={8}
        align="start"
        onInteractOutside={(e) => {
          // An outside-click on a dirty draft would silently discard it. Keep
          // the popover open and ask first; a clean draft closes as usual.
          // (Esc and the editor's Cancel are explicit dismissals — left
          // unguarded so the deliberate escape hatch stays one action.)
          if (dirtyRef.current) {
            e.preventDefault();
            setShowDiscard(true);
          }
        }}
      >
        <CellEntryEditor
          date={date}
          entry={entry}
          customerIsInternal={customerIsInternal}
          defaultBillable={defaultBillable}
          productDescription={productDescription}
          submitError={submitError}
          otherHoursOnDay={otherHoursOnDay}
          readOnly={readOnly}
          onDirtyChange={handleDirtyChange}
          onCreate={(value) => {
            onCreate(
              {
                customerId: rowCustomerId,
                productId: rowProductId,
                engagementId: rowEngagementId,
                lineKey,
                isManualLine,
                dayIndex,
                date,
              },
              value,
            );
            closeCell();
          }}
          onPatch={(id, value) => {
            onPatch(id, value);
            closeCell();
          }}
          onDelete={(id) => {
            onDelete(id);
            closeCell();
          }}
          onClose={closeCell}
        />
        {/* Submitted cells open read-only; offer a per-entry recall to draft.
            Approved entries can no longer be recalled by the submitter — only
            the approver can reverse them. Suppressed once the period is locked
            (recall is blocked there unless admin-unlocked). */}
        {entry && entry.status === 'submitted' && !periodLocked ? (
          <div className="mt-2 flex justify-end border-t border-border pt-2">
            <RecallEntryButton
              onConfirm={() => {
                onRecall(entry.id);
                closeCell();
              }}
            />
          </div>
        ) : null}
      </PopoverContent>
      <DiscardChangesDialog
        open={showDiscard}
        onKeepEditing={() => setShowDiscard(false)}
        onDiscard={closeCell}
      />
    </Popover>
  );
});

function TotalsFooter({
  dailyTotals,
  weekTotal,
  expectedPerDay,
  expectedPerWeek,
  dates,
}: {
  dailyTotals: number[];
  weekTotal: number;
  expectedPerDay: number;
  expectedPerWeek: number;
  dates: string[];
}) {
  // A SINGLE row: each day's total and its over/under delta are stacked inside
  // one cell rather than living on two separate rows. Two rows inside the
  // pinned <tfoot> left a border-spacing gap between them — in the separated-
  // border model that strip paints the (transparent) table background and
  // neither the row nor the row-group background covers it, so scrolled rows
  // bled through the seam. One row has no internal seam and behaves exactly
  // like the sticky header.
  return (
    <TableRow style={{ backgroundColor: 'color-mix(in oklab, var(--muted) 25%, var(--card))' }}>
      <TableCell
        colSpan={2}
        className="align-middle text-xs uppercase tracking-wide text-muted-foreground"
      >
        <div className="font-medium text-foreground">Daily totals</div>
        <div className="mt-1">Over / under</div>
      </TableCell>
      {dailyTotals.map((t, i) => {
        const isWeekend = i >= 5;
        const expected = isWeekend ? 0 : expectedPerDay;
        const delta = t - expected;
        return (
          <TableCell key={`totals-${dates[i] ?? i}`} className="text-center align-middle">
            {/* sr-only metric labels: each cell stacks two values, so a screen
                reader would otherwise announce two bare numbers per day (the day
                itself comes from the column header). */}
            <div className="text-sm font-medium">
              <span className="sr-only">Daily total: </span>
              {t > 0 ? formatHours(t) : '—'}
            </div>
            <div className={`mt-1 text-xs ${deltaTextClass(delta, isWeekend)}`}>
              <span className="sr-only">Over / under: </span>
              {t === 0 && isWeekend ? '—' : formatDelta(delta)}
            </div>
          </TableCell>
        );
      })}
      <TableCell className="text-right align-middle">
        <div className="text-sm font-semibold">
          <span className="sr-only">Week total: </span>
          {formatHours(weekTotal)}
        </div>
        <div className={`mt-1 text-xs font-medium ${deltaTextClass(weekTotal - expectedPerWeek)}`}>
          <span className="sr-only">Week over / under: </span>
          {formatDelta(weekTotal - expectedPerWeek)}
        </div>
      </TableCell>
      <TableCell />
    </TableRow>
  );
}
